import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { List, Button, Modal, Input, Select, message, Popconfirm, Form, Tooltip, Collapse, Tag, Drawer, Divider, Dropdown, Menu } from 'antd';
import { PlusOutlined, SaveOutlined, DeleteOutlined, CopyOutlined, SettingOutlined, EditOutlined, MenuOutlined, CloseOutlined, UndoOutlined, RedoOutlined, FunctionOutlined, ApartmentOutlined } from '@ant-design/icons';
// Add History icon
import { HistoryOutlined, DiffOutlined } from '@ant-design/icons';
import dagre from 'dagre';
import ReactFlow, {
  addEdge,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  useReactFlow,
  NodeResizer,
} from 'reactflow';
import 'reactflow/dist/style.css';
import './WorkflowEditor.css';
import { workflowsAPI, modulesAPI, usersAPI, contextAPI } from '../services/api';
import { taskApi } from '../utils/api';
import RegexTester from './workflowcomponents/RegexTester';
import RegexContextTransform from './workflowcomponents/RegexContextTransform';
import RegexTransformList from './workflowcomponents/RegexTransformList';
import ContextGroupList from './workflowcomponents/ContextGroupList';
import StorageConfigSelector from './workflowcomponents/StorageConfigSelector';
import StatusSelector from './workflowcomponents/StatusSelector';
import KeyValueEditor from './workflowcomponents/KeyValueEditor';
import CustomNode from './WorkflowEditor/components/CustomNode';
import CodeEditor from './CodeEditor';

const { Option } = Select;
const { Panel } = Collapse;

// Component to render workflow diff
const DiffViewer = ({ diff }) => {
  if (!diff || Object.keys(diff).length === 0) {
    return <p style={{ color: '#999', fontStyle: 'italic' }}>No changes in this version</p>;
  }

  const panels = [];

  // Name change
  if (diff.name_changed) {
    panels.push(
      <Panel header="Workflow Name" key="name">
        <div>
          <Tag color="red">From: {diff.name_changed.from || '(empty)'}</Tag>
          <Tag color="green">To: {diff.name_changed.to || '(empty)'}</Tag>
        </div>
      </Panel>
    );
  }

  // Node changes
  if (diff.nodes) {
    const nodeContent = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {diff.nodes.added && diff.nodes.added.length > 0 && (
          <div>
            <h4 style={{ color: 'green', margin: 0 }}>Added Nodes ({diff.nodes.added.length})</h4>
            {diff.nodes.added.map((node, i) => (
              <div key={i} style={{ padding: 8, border: '1px solid #52c41a', borderRadius: 4, marginTop: 8 }}>
                <strong>{node.data?.displayName || node.data?.label || 'Unnamed Node'}</strong>
                <div style={{ fontSize: 12, color: '#666' }}>ID: {node.id}</div>
              </div>
            ))}
          </div>
        )}
        
        {diff.nodes.removed && diff.nodes.removed.length > 0 && (
          <div>
            <h4 style={{ color: 'red', margin: 0 }}>Removed Nodes ({diff.nodes.removed.length})</h4>
            {diff.nodes.removed.map((node, i) => (
              <div key={i} style={{ padding: 8, border: '1px solid #ff4d4f', borderRadius: 4, marginTop: 8 }}>
                <strong>{node.data?.displayName || node.data?.label || 'Unnamed Node'}</strong>
                <div style={{ fontSize: 12, color: '#666' }}>ID: {node.id}</div>
              </div>
            ))}
          </div>
        )}
        
        {diff.nodes.modified && diff.nodes.modified.length > 0 && (
          <div>
            <h4 style={{ color: 'orange', margin: 0 }}>Modified Nodes ({diff.nodes.modified.length})</h4>
            {diff.nodes.modified.map((nodeChange, i) => (
              <div key={i} style={{ padding: 8, border: '1px solid #fa8c16', borderRadius: 4, marginTop: 8 }}>
                <strong>Node: {nodeChange.id}</strong>
                {nodeChange.changes.displayName && (
                  <div>
                    <span style={{ fontSize: 12 }}>Name: </span>
                    <Tag color="red" size="small">{nodeChange.changes.displayName.from}</Tag>
                    <span>→</span>
                    <Tag color="green" size="small">{nodeChange.changes.displayName.to}</Tag>
                  </div>
                )}
                {nodeChange.changes.icon && (
                  <div>
                    <span style={{ fontSize: 12 }}>Icon: </span>
                    <Tag color="red" size="small">{nodeChange.changes.icon.from}</Tag>
                    <span>→</span>
                    <Tag color="green" size="small">{nodeChange.changes.icon.to}</Tag>
                  </div>
                )}
                {nodeChange.changes.position && (
                  <div style={{ fontSize: 12, color: '#666' }}>
                    Position changed: ({nodeChange.changes.position.from.x}, {nodeChange.changes.position.from.y}) → 
                    ({nodeChange.changes.position.to.x}, {nodeChange.changes.position.to.y})
                  </div>
                )}
                {nodeChange.changes.settings && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 'bold' }}>Settings Changes:</div>
                    {nodeChange.changes.settings.added && (
                      <div>
                        <span style={{ fontSize: 11, color: 'green' }}>Added: </span>
                        {Object.entries(nodeChange.changes.settings.added).map(([k, v]) => (
                          <Tag key={k} color="green" size="small">{k}: {String(v)}</Tag>
                        ))}
                      </div>
                    )}
                    {nodeChange.changes.settings.removed && (
                      <div>
                        <span style={{ fontSize: 11, color: 'red' }}>Removed: </span>
                        {nodeChange.changes.settings.removed.map(k => (
                          <Tag key={k} color="red" size="small">{k}</Tag>
                        ))}
                      </div>
                    )}
                    {nodeChange.changes.settings.changed && (
                      <div>
                        <span style={{ fontSize: 11, color: 'orange' }}>Changed: </span>
                        {Object.entries(nodeChange.changes.settings.changed).map(([k, change]) => (
                          <div key={k} style={{ fontSize: 11 }}>
                            {k}: <Tag color="red" size="small">{String(change.from)}</Tag> → <Tag color="green" size="small">{String(change.to)}</Tag>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
    
    panels.push(
      <Panel header={`Nodes (${(diff.nodes.added?.length || 0) + (diff.nodes.removed?.length || 0) + (diff.nodes.modified?.length || 0)} changes)`} key="nodes">
        {nodeContent}
      </Panel>
    );
  }

  // Edge changes
  if (diff.edges) {
    const edgeContent = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {diff.edges.added && diff.edges.added.length > 0 && (
          <div>
            <h4 style={{ color: 'green', margin: 0 }}>Added Connections ({diff.edges.added.length})</h4>
            {diff.edges.added.map((edge, i) => (
              <div key={i} style={{ padding: 8, border: '1px solid #52c41a', borderRadius: 4, marginTop: 8 }}>
                <div style={{ fontSize: 12 }}>{edge.source} → {edge.target}</div>
                {edge.id && <div style={{ fontSize: 11, color: '#666' }}>ID: {edge.id}</div>}
              </div>
            ))}
          </div>
        )}
        
        {diff.edges.removed && diff.edges.removed.length > 0 && (
          <div>
            <h4 style={{ color: 'red', margin: 0 }}>Removed Connections ({diff.edges.removed.length})</h4>
            {diff.edges.removed.map((edge, i) => (
              <div key={i} style={{ padding: 8, border: '1px solid #ff4d4f', borderRadius: 4, marginTop: 8 }}>
                <div style={{ fontSize: 12 }}>{edge.source} → {edge.target}</div>
                {edge.id && <div style={{ fontSize: 11, color: '#666' }}>ID: {edge.id}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
    
    panels.push(
      <Panel header={`Connections (${(diff.edges.added?.length || 0) + (diff.edges.removed?.length || 0)} changes)`} key="edges">
        {edgeContent}
      </Panel>
    );
  }

  return (
    <Collapse ghost>
      {panels}
    </Collapse>
  );
};

// Note Node component for background annotations
const NoteNode = React.memo(({ id, data, selected }) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [editText, setEditText] = React.useState(data?.noteText || '');
  const textareaRef = React.useRef(null);
  const noteText = data?.noteText || 'Click to edit note';
  const backgroundColor = data?.backgroundColor || '#fff9c4';
  const textColor = data?.textColor || '#333';

  // Handle clicking on text to edit
  const handleTextClick = (e) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditText(noteText);
  };

  // Handle saving text on blur or Escape
  const handleTextBlur = () => {
    if (isEditing) {
      // Update the node data
      const event = new CustomEvent('updateNoteText', {
        detail: { nodeId: id, text: editText }
      });
      window.dispatchEvent(event);
      setIsEditing(false);
    }
  };

  // Focus textarea when entering edit mode
  React.useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsEditing(false);
      setEditText(noteText);
    } else if (e.key === 'Enter' && e.ctrlKey) {
      handleTextBlur();
    }
  };

  return (
    <>
      <NodeResizer
        color="#ffc107"
        isVisible={selected}
        minWidth={150}
        minHeight={100}
      />
      <div
        style={{
          background: backgroundColor,
          borderRadius: 8,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'flex-start',
          cursor: isEditing ? 'text' : 'move',
          boxShadow: selected 
            ? `0 4px 20px rgba(255, 193, 7, 0.3)` 
            : '0 2px 8px rgba(0, 0, 0, 0.1)',
          transition: 'box-shadow 0.2s ease',
          position: 'relative',
          padding: 16,
          border: selected ? '2px solid #ffc107' : '2px solid #f0f0f0',
          opacity: 0.95,
          overflow: 'hidden',
          zIndex: -1
        }}
      >
        <div style={{ 
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          color: textColor,
          fontSize: 14,
          lineHeight: 1.5,
          overflow: 'auto'
        }}>
          {/* Note icon */}
          <div style={{ 
            fontSize: 20,
            marginBottom: 4,
            opacity: 0.7,
            pointerEvents: 'none'
          }}>📝</div>
          
          {isEditing ? (
            <textarea
              ref={textareaRef}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onBlur={handleTextBlur}
              onKeyDown={handleKeyDown}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                background: 'transparent',
                color: textColor,
                fontSize: 14,
                lineHeight: 1.5,
                fontFamily: 'inherit',
                resize: 'none',
                outline: 'none',
                padding: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
              placeholder="Enter note text..."
            />
          ) : (
            <div
              onClick={handleTextClick}
              style={{
                width: '100%',
                height: '100%',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                cursor: 'text',
                userSelect: 'none'
              }}
            >
              {noteText || 'Click to edit note'}
            </div>
          )}
        </div>
      </div>
    </>
  );
});

// Component that has access to ReactFlow instance
const WorkflowCanvas = React.forwardRef(({ 
  nodes, 
  edges, 
  onNodesChangeWithSelection, 
  onEdgesChangeWithHistory,
  onConnect,
  onConnectStart,
  onConnectEnd,
  onDrop,
  onDragOver,
  onNodeDoubleClick,
  onNodeClick,
  onNodeContextMenu,
  onPaneClick,
  onPaneContextMenu,
  onSelectionChange,
  nodeTypes,
  snapGrid,
  reactFlowStyle,
  connectionLineStyle,
  defaultEdgeOptions,
  controlsStyle,
  miniMapStyle,
  nodeColor
}, ref) => {
  const reactFlowInstance = useReactFlow();

  // Expose ReactFlow methods to parent component
  React.useImperativeHandle(ref, () => ({
    fitView: () => reactFlowInstance.fitView({ padding: 0.1, duration: 800 }),
  }), [reactFlowInstance]);

  // Expose screen to flow position conversion for drag/drop
  useEffect(() => {
    window.screenToFlowPosition = (screenPosition) => {
      return reactFlowInstance.screenToFlowPosition(screenPosition);
    };
  }, [reactFlowInstance]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChangeWithSelection}
      onEdgesChange={onEdgesChangeWithHistory}
      onConnect={onConnect}
      onConnectStart={onConnectStart}
      onConnectEnd={onConnectEnd}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onNodeDoubleClick={onNodeDoubleClick}
      onNodeClick={onNodeClick}
      onNodeContextMenu={onNodeContextMenu}
      onPaneClick={onPaneClick}
      onPaneContextMenu={onPaneContextMenu}
      onSelectionChange={onSelectionChange}
      nodeTypes={nodeTypes}
      snapToGrid
      snapGrid={snapGrid}
      style={reactFlowStyle}
      connectionLineStyle={connectionLineStyle}
      defaultEdgeOptions={defaultEdgeOptions}
      connectionMode="loose"
      onlyRenderVisibleElements={false}
      panOnDrag={true}
      panOnScroll={false}
      selectNodesOnDrag={false}
      nodesFocusable={false}
      edgesFocusable={true}
      elementsSelectable={true}
      nodesConnectable={true}
      nodesDraggable={true}
      edgesUpdatable={true}
      preventScrolling={true}
      autoPanOnConnect={false}
      autoPanOnNodeDrag={false}
      attributionPosition="bottom-left"
      proOptions={{ hideAttribution: true }}
      disableKeyboardA11y={true}
    >
      <Controls style={controlsStyle} />
      <MiniMap 
        style={miniMapStyle}
        nodeColor={nodeColor}
        pannable={true}
      />
      <Background 
        variant="dots" 
        gap={20} 
        size={1} 
        color="#ddd"
      />
    </ReactFlow>
  );
});

// Template Variable Button Component
const TemplateVariableButton = ({ onInsert, inputRef, disabled = false, onBrowseNodeContext }) => {
  const [contextSettings, setContextSettings] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadContextData();
  }, []);

  const loadContextData = async () => {
    try {
      setLoading(true);
      const [contextResponse, usersResponse] = await Promise.all([
        contextAPI.getGlobalContext(),
        usersAPI.getUsers()
      ]);
      setContextSettings(contextResponse.data || []);
      // Filter users to get only clients
      const allUsers = usersResponse.data || [];
      const clientUsers = allUsers.filter(u => u.role === 'client');
      setClients(clientUsers);
    } catch (error) {
      console.error('Failed to load context data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTemplateVariables = () => {
    const variables = [];

    // System variables (always available)
    variables.push({
      key: 'system-header',
      label: 'System Variables',
      type: 'header'
    });
    variables.push(
      { key: '{system.default_timezone}', label: 'Default Timezone', type: 'system' },
      { key: '{system.max_concurrent_jobs}', label: 'Max Concurrent Jobs', type: 'system' },
      { key: '{system.default_file_retention_days}', label: 'File Retention Days', type: 'system' }
    );

    // Workflow template variables (always available)
    variables.push({
      key: 'workflow-header',
      label: 'Workflow Variables',
      type: 'header'
    });
    variables.push(
      { key: '{timestamp}', label: 'Current Timestamp (20250821_212816)', type: 'workflow' },
      { key: '{date}', label: 'Current Date (20250821)', type: 'workflow' },
      { key: '{time}', label: 'Current Time (212816)', type: 'workflow' },
      { key: '{datetime}', label: 'Current DateTime (ISO format)', type: 'workflow' },
      { key: '{workflow.job_name_pattern}', label: 'Job Name Pattern', type: 'workflow' }
    );

    // Common aliases (always available)
    variables.push({
      key: 'aliases-header',
      label: 'Common Aliases',
      type: 'header'
    });
    variables.push(
      { key: '{jobprefix}', label: 'Job Prefix (client.jobprefix)', type: 'alias' },
      { key: '{storage_path}', label: 'Storage Path (client.storage_path)', type: 'alias' },
      { key: '{notification_email}', label: 'Notification Email (client.notification_email)', type: 'alias' }
    );

    // Client variables (if available from API)
    if (contextSettings.length > 0) {
      const clientNamespaces = [...new Set(contextSettings
        .filter(c => c.namespace.startsWith('client.'))
        .map(c => c.namespace)
      )];

      clientNamespaces.forEach(namespace => {
        const clientId = namespace.split('.')[1];
        const client = clients.find(c => c.id === parseInt(clientId));
        const clientName = client ? client.name : `Client ${clientId}`;
        
        variables.push({
          key: `client-${clientId}-header`,
          label: `${clientName} Specific Variables`,
          type: 'header'
        });

        // Add specific client variables
        const clientSettings = contextSettings.filter(c => c.namespace === namespace);
        clientSettings.forEach(setting => {
          variables.push({
            key: `{client.${setting.context_key}}`,
            label: `${setting.context_key} - ${setting.description || 'No description'}`,
            type: 'client',
            clientId
          });
        });
      });
    } else {
      // Fallback if no context data is loaded
      variables.push({
        key: 'fallback-header',
        label: 'Example Client Variables',
        type: 'header'
      });
      variables.push(
        { key: '{client.jobprefix}', label: 'Client Job Prefix', type: 'example' },
        { key: '{client.storage_path}', label: 'Client Storage Path', type: 'example' },
        { key: '{client.notification_email}', label: 'Client Notification Email', type: 'example' },
        { key: '{client.name}', label: 'Client Name', type: 'example' }
      );
    }

    return variables;
  };

  const handleVariableSelect = (variable) => {
    if (variable.type === 'header') return;
    
    // If we have an inputRef, try the direct DOM approach
    if (inputRef && inputRef.current) {
      const input = inputRef.current.input || inputRef.current;
      
      // Get current cursor position and value
      const cursorStart = input.selectionStart || 0;
      const cursorEnd = input.selectionEnd || cursorStart;
      const currentValue = input.value || '';
      
      // Create new value with variable inserted at cursor position
      const newValue = 
        currentValue.slice(0, cursorStart) + 
        variable.key + 
        currentValue.slice(cursorEnd);
      
      // Direct DOM manipulation
      input.value = newValue;
      
      // Create and dispatch events
      const inputEvent = new Event('input', { bubbles: true });
      const changeEvent = new Event('change', { bubbles: true });
      input.dispatchEvent(inputEvent);
      input.dispatchEvent(changeEvent);
      
      // Call onInsert callback
      if (onInsert) {
        onInsert(newValue);
      }
      
      // Set cursor position
      const newPosition = cursorStart + variable.key.length;
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(newPosition, newPosition);
      }, 10);
    } else {
      // No inputRef provided, just use the onInsert callback
      if (onInsert) {
        onInsert(variable.key);
      }
    }
  };

  const menu = (
    <Menu
      style={{ maxHeight: '300px', overflowY: 'auto', minWidth: '250px' }}
      onClick={({ key }) => {
        if (key === '__browse_node_context__') {
          if (onBrowseNodeContext) {
            onBrowseNodeContext();
          }
          return;
        }

        const variable = getTemplateVariables().find(v => v.key === key);
        if (variable) {
          handleVariableSelect(variable);
        }
      }}
    >
      {onBrowseNodeContext && (
        <>
          <Menu.Item key="__browse_node_context__">
            Browse Node Context…
          </Menu.Item>
          <Menu.Divider />
        </>
      )}

      {getTemplateVariables().map(variable => 
        variable.type === 'header' ? (
          <Menu.ItemGroup key={variable.key} title={variable.label} />
        ) : (
          <Menu.Item key={variable.key}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{variable.key}</span>
              <span style={{ fontSize: '11px', color: '#666', marginLeft: '8px' }}>
                {variable.label}
              </span>
            </div>
          </Menu.Item>
        )
      )}
    </Menu>
  );

  return (
    <Dropdown overlay={menu} trigger={['click']} disabled={disabled || loading}>
      <Button
        icon={<FunctionOutlined />}
        size="small"
        style={{ border: 'none', background: 'transparent' }}
        title="Insert template variable"
        disabled={disabled || loading}
      />
    </Dropdown>
  );
};

// Enhanced Input with Template Variable Support
const InputWithTemplateButton = React.forwardRef(({ ...props }, ref) => {
  const inputRef = useRef(null);
  const actualRef = ref || inputRef;
  
  const handleTemplateInsert = (newValue) => {
    // Trigger form update
    if (props.onChange) {
      props.onChange({ target: { value: newValue } });
    }
  };
  
  return (
    <Input
      {...props}
      ref={actualRef}
      suffix={
        <TemplateVariableButton 
          inputRef={actualRef}
          onInsert={handleTemplateInsert}
        />
      }
    />
  );
});

// Enhanced TextArea with Template Variable Support
// eslint-disable-next-line no-unused-vars
const TextAreaWithTemplateButton = React.forwardRef(({ ...props }, ref) => {
  const inputRef = useRef(null);
  const actualRef = ref || inputRef;
  
  const handleTemplateInsert = (newValue) => {
    // Trigger form update
    if (props.onChange) {
      props.onChange({ target: { value: newValue } });
    }
  };
  
  return (
    <div style={{ position: 'relative' }}>
      <Input.TextArea
        {...props}
        ref={actualRef}
      />
      <div style={{ 
        position: 'absolute', 
        top: '8px', 
        right: '8px', 
        zIndex: 1,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: '4px',
        padding: '2px'
      }}>
        <TemplateVariableButton 
          inputRef={actualRef}
          onInsert={handleTemplateInsert}
        />
      </div>
    </div>
  );
});

const WorkflowEditor = () => {
  // Suppress ResizeObserver loop error (common with ReactFlow)
  useEffect(() => {
    const handleResizeObserverError = (e) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
        const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none');
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none');
        }
      }
    };
    window.addEventListener('error', handleResizeObserverError);
    return () => window.removeEventListener('error', handleResizeObserverError);
  }, []);

  // Ref to access ReactFlow methods
  const reactFlowRef = useRef(null);

  // Memoize static objects to prevent recreation on every render
  const defaultEdgeOptions = useMemo(() => ({
    style: { stroke: '#999', strokeWidth: 2 },
    type: 'bezier',
    markerEnd: {
      type: 'arrowclosed',
      color: '#999'
    }
  }), []);

  const connectionLineStyle = useMemo(() => ({ 
    stroke: '#999', 
    strokeWidth: 2 
  }), []);

  const snapGrid = useMemo(() => [20, 20], []);

  // Memoize style objects for Controls and MiniMap
  const controlsStyle = useMemo(() => ({
    bottom: 20,
    left: 20
  }), []);

  const miniMapStyle = useMemo(() => ({
    bottom: 20,
    right: 20,
    background: '#fff',
    border: '1px solid #e8e8e8'
  }), []);

  const reactFlowStyle = useMemo(() => ({ 
    background: '#fafafa' 
  }), []);

  // Memoize nodeColor function
  const nodeColor = useCallback((node) => {
    const module = node.data?.module;
    const type = module?.type || module?.module_type || '';
    switch (type.toLowerCase()) {
      case 'input':
      case 'source':
        return '#00d4aa';
      case 'output':
      case 'destination':
        return '#ff6b6b';
      case 'processor':
      case 'transform':
        return '#4dabf7';
      case 'condition':
      case 'logic':
        return '#ffd43b';
      case 'storage':
        return '#9775fa';
      default:
        return '#868e96';
    }
  }, []);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [modules, setModules] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [workflowName, setWorkflowName] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [showOverview, setShowOverview] = useState(true); // controls left panel mode
  // Copy/paste state
  const [copiedNode, setCopiedNode] = useState(null);
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [selectedEdges, setSelectedEdges] = useState([]);
  
  // Update selectedNodes when selectedNodeId changes
  useEffect(() => {
    if (selectedNodeId) {
      setSelectedNodes([selectedNodeId]);
    } else {
      setSelectedNodes([]);
    }
  }, [selectedNodeId]);
  // Version history state
  const [versionHistory, setVersionHistory] = useState([]);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [viewingVersion, setViewingVersion] = useState(null); // object with version data
  const [isReadonlyVersion, setIsReadonlyVersion] = useState(false);
  // New: rename workflow modal state
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renameWorkflowId, setRenameWorkflowId] = useState(null);
  const [renameWorkflowName, setRenameWorkflowName] = useState('');
  // Diff modal state
  const [isDiffModalOpen, setIsDiffModalOpen] = useState(false);
  const [currentDiff, setCurrentDiff] = useState(null);
  const [diffVersion, setDiffVersion] = useState(null);
  // Sidebar state
  const [sidebarVisible, setSidebarVisible] = useState(false);
  // Node picker state
  const [nodePickerVisible, setNodePickerVisible] = useState(false);
  const [nodePickerPosition, setNodePickerPosition] = useState({ x: 0, y: 0 }); // screen position for picker display
  const [nodeDropPosition, setNodeDropPosition] = useState({ x: 0, y: 0 }); // flow position for node placement
  const [connectionSource, setConnectionSource] = useState(null);
  const [showOnlyInputNodes, setShowOnlyInputNodes] = useState(false);
  
  // Context menu state
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [contextMenuFlowPosition, setContextMenuFlowPosition] = useState({ x: 0, y: 0 });
  
  // Node context menu state
  const [nodeContextMenuVisible, setNodeContextMenuVisible] = useState(false);
  const [nodeContextMenuPosition, setNodeContextMenuPosition] = useState({ x: 0, y: 0 });
  const [contextMenuNode, setContextMenuNode] = useState(null);
  
  // Node search state
  const [nodeSearchTerm, setNodeSearchTerm] = useState('');
  
  // Recent workflows state
  const [recentWorkflows, setRecentWorkflows] = useState([]);
  const [recentWorkflowsLoaded, setRecentWorkflowsLoaded] = useState(false);
  
  // Undo/Redo state
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isUndoRedoAction, setIsUndoRedoAction] = useState(false);
  const positionSaveTimeout = useRef(null);

  // Node editing state
  const [isNodeModalOpen, setIsNodeModalOpen] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [editingModule, setEditingModule] = useState(null);
  const [newFieldNames, setNewFieldNames] = useState([]); // Track which fields are new after update
  const [nodeForm] = Form.useForm();

  // Global node context browser
  const [isNodeContextBrowserOpen, setIsNodeContextBrowserOpen] = useState(false);
  const [nodeContextBrowserEntries, setNodeContextBrowserEntries] = useState([]);
  const [nodeContextBrowserSearch, setNodeContextBrowserSearch] = useState('');
  const [nodeContextBrowserLoading, setNodeContextBrowserLoading] = useState(false);
  const lastFocusedNodeFieldRef = useRef(null);
  
  // Note editing state
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteForm] = Form.useForm();
  
  // Watch form values to trigger re-renders for conditional fields
  const formValues = Form.useWatch([], nodeForm);

  const copyToClipboard = useCallback(async (text) => {
    if (!text) return false;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (e) {
      // fall through to execCommand
    }

    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch (e) {
      return false;
    }
  }, []);

  const parseKeyValueText = useCallback((text) => {
    if (!text || String(text).trim() === '') return [];

    const lines = String(text)
      .split('\n')
      .map(l => l.trim())
      .filter(l => l !== '');

    return lines.map((line) => {
      const colonIndex = line.indexOf(':');
      if (colonIndex === -1) {
        return { key: line.trim(), value: '' };
      }
      return {
        key: line.substring(0, colonIndex).trim(),
        value: line.substring(colonIndex + 1).trim(),
      };
    }).filter(p => p.key);
  }, []);

  const collectNodeContextEntries = useCallback(() => {
    if (!editingModule?.fields) return [];

    const entries = [];
    editingModule.fields
      .filter(f => f.field_type === 'key_value')
      .forEach((f) => {
        const rawValue = nodeForm.getFieldValue(f.name) ?? f.default_value ?? '';
        const pairs = parseKeyValueText(rawValue);
        pairs.forEach((p) => {
          const token = `{context.${p.key}}`;
          entries.push({
            sourceField: f.name,
            key: p.key,
            value: p.value,
            token,
            sources: ['local_node'],
          });
        });
      });

    return entries;
  }, [editingModule, nodeForm, parseKeyValueText]);

  const mergeContextEntries = useCallback((localEntries, remoteEntries) => {
    const map = new Map();
    const put = (entry) => {
      if (!entry?.token) return;
      const existing = map.get(entry.token);
      if (!existing) {
        map.set(entry.token, {
          ...entry,
          sources: Array.isArray(entry.sources) ? entry.sources : (entry.sources ? [entry.sources] : []),
        });
        return;
      }

      const existingSources = new Set(existing.sources || []);
      (Array.isArray(entry.sources) ? entry.sources : (entry.sources ? [entry.sources] : [])).forEach(s => existingSources.add(s));
      const merged = {
        ...existing,
        ...entry,
        sources: Array.from(existingSources),
      };

      // Prefer a non-empty value
      if ((existing.value === undefined || existing.value === null || existing.value === '') && (entry.value !== undefined && entry.value !== null && entry.value !== '')) {
        merged.value = entry.value;
      } else {
        merged.value = existing.value;
      }

      map.set(entry.token, merged);
    };

    (localEntries || []).forEach(put);
    (remoteEntries || []).forEach((e) => {
      put({
        key: e.key,
        value: e.value,
        token: e.token || `{context.${e.key}}`,
        sourceField: e.meta?.field || e.sourceField,
        sources: e.sources || ['backend'],
      });
    });

    return Array.from(map.values()).sort((a, b) => (a.key || '').localeCompare((b.key || ''), undefined, { sensitivity: 'base' }));
  }, []);

  const insertIntoLastFocusedNodeField = useCallback((textToInsert) => {
    const fieldName = lastFocusedNodeFieldRef.current;
    if (!fieldName) {
      return;
    }
    const currentValue = nodeForm.getFieldValue(fieldName) || '';
    nodeForm.setFieldsValue({ [fieldName]: String(currentValue) + String(textToInsert) });
  }, [nodeForm]);

  const openNodeContextBrowser = useCallback(async () => {
    const localEntries = collectNodeContextEntries();
    if (localEntries.length === 0) {
      message.info('No context keys found in this node yet');
    }
    setNodeContextBrowserSearch('');
    setIsNodeContextBrowserOpen(true);
    setNodeContextBrowserEntries(localEntries);

    if (!selectedWorkflow || !editingNode?.id) {
      return;
    }

    try {
      setNodeContextBrowserLoading(true);
      const resp = await workflowsAPI.getNodeContextBrowser(selectedWorkflow, editingNode.id, {
        include_saved: 1,
        include_historical: 1,
        job_limit: 25,
      });
      const remote = resp.data?.entries || [];
      setNodeContextBrowserEntries(mergeContextEntries(localEntries, remote));
    } catch (e) {
      console.error('Failed to load node context browser data:', e);
      message.warning('Could not load saved/historical context from backend');
    } finally {
      setNodeContextBrowserLoading(false);
    }
  }, [collectNodeContextEntries, selectedWorkflow, editingNode, mergeContextEntries]);

  const filteredNodeContextBrowserEntries = useMemo(() => {
    const q = String(nodeContextBrowserSearch ?? '').trim().toLowerCase();
    if (!q) return nodeContextBrowserEntries;

    return (Array.isArray(nodeContextBrowserEntries) ? nodeContextBrowserEntries : []).filter(e =>
      String(e?.key ?? '').toLowerCase().includes(q) ||
      String(e?.value ?? '').toLowerCase().includes(q) ||
      String(e?.sourceField ?? '').toLowerCase().includes(q)
    );
  }, [nodeContextBrowserEntries, nodeContextBrowserSearch]);
  
  // Clear values for hidden conditional fields
  useEffect(() => {
    if (editingModule && editingModule.fields) {
      const fieldsToUnset = [];
      
      editingModule.fields.forEach(field => {
        if (field.conditional) {
          const { field: conditionField, value: conditionValue, operator } = field.conditional;
          const currentFormValue = nodeForm.getFieldValue(conditionField);
          
          let shouldShow = false;
          switch (operator) {
            case '!=':
            case 'not_equals':
              shouldShow = currentFormValue !== conditionValue;
              break;
            case 'in':
              shouldShow = Array.isArray(conditionValue) && conditionValue.includes(currentFormValue);
              break;
            case 'not_in':
              shouldShow = Array.isArray(conditionValue) && !conditionValue.includes(currentFormValue);
              break;
            default:
              shouldShow = currentFormValue === conditionValue;
              break;
          }
          
          // If field should be hidden, clear its value
          if (!shouldShow && nodeForm.getFieldValue(field.name) !== undefined) {
            fieldsToUnset.push(field.name);
          }
        }
      });
      
      // Unset fields that should be hidden
      if (fieldsToUnset.length > 0) {
        const currentValues = nodeForm.getFieldsValue();
        fieldsToUnset.forEach(fieldName => {
          delete currentValues[fieldName];
        });
        nodeForm.setFieldsValue(currentValues);
      }
    }
  }, [formValues, editingModule, nodeForm]);
  
  // Ensure form is properly initialized when modal opens
  useEffect(() => {
    if (isNodeModalOpen && editingNode && editingModule) {
      // Force a small delay to ensure form is ready
      setTimeout(() => {
        const currentValues = nodeForm.getFieldsValue();
        console.log('Modal opened - current form values:', currentValues);
        
        // If monitoring_mode is not set, set it to default
        if (!currentValues.monitoring_mode && editingModule.fields) {
          const monitoringModeField = editingModule.fields.find(f => f.name === 'monitoring_mode');
          if (monitoringModeField && monitoringModeField.default_value) {
            console.log('Setting default monitoring_mode:', monitoringModeField.default_value);
            nodeForm.setFieldsValue({ 
              ...currentValues, 
              monitoring_mode: monitoringModeField.default_value 
            });
          }
        }
      }, 100);
    }
  }, [isNodeModalOpen, editingNode, editingModule, nodeForm]);

  // Filter and sort modules based on search term
  const filteredAndSortedModules = useMemo(() => {
    let filtered = modules;

    // Apply search filter - only by module name
    if (nodeSearchTerm.trim()) {
      const searchLower = nodeSearchTerm.toLowerCase();
      filtered = modules.filter(module => 
        module.name.toLowerCase().includes(searchLower)
      );
    }

    // Sort alphabetically by name
    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [modules, nodeSearchTerm]);

  // Load modules and clients from backend
  useEffect(() => {
    // Load modules from backend
    const fetchModules = async () => {
      try {
        const response = await modulesAPI.getModules();
        setModules(response.data);
      } catch (error) {
        setModules([]);
      }
    };

    // Load clients from backend
    const fetchClients = async () => {
      try {
        const response = await taskApi.getClients();
        setClients(response.clients || []);
      } catch (error) {
        setClients([]);
      }
    };

    // Load recent workflows from localStorage
    const loadRecentWorkflows = () => {
      try {
        const saved = localStorage.getItem('recentWorkflows');
        if (saved) {
          setRecentWorkflows(JSON.parse(saved));
        }
      } catch (error) {
        console.warn('Failed to load recent workflows from localStorage:', error);
      } finally {
        setRecentWorkflowsLoaded(true);
      }
    };

    fetchModules();
    fetchClients();
    loadRecentWorkflows();
  }, []);

  // Save recent workflows to localStorage when they change
  useEffect(() => {
    // Only save if we've loaded the initial data from localStorage
    if (recentWorkflowsLoaded) {
      try {
        localStorage.setItem('recentWorkflows', JSON.stringify(recentWorkflows));
      } catch (error) {
        console.warn('Failed to save recent workflows to localStorage:', error);
      }
    }
  }, [recentWorkflows, recentWorkflowsLoaded]);

  // Load workflows when client is selected
  useEffect(() => {
    if (selectedClient) {
      // Load workflows for selected client from backend
      const fetchWorkflows = async () => {
        try {
          const response = await workflowsAPI.getWorkflows(selectedClient);
          setWorkflows(response.data);
        } catch (error) {
          setWorkflows([]);
        }
      };
      fetchWorkflows();
    }
  }, [selectedClient]);

  // useReactFlow hook for coordinate transformation
  useEffect(() => {
    // Load modules from backend
    const fetchModules = async () => {
      try {
        const response = await modulesAPI.getModules();
        setModules(response.data);
      } catch (error) {
        setModules([]);
      }
    };

    // Load clients from backend
    const fetchClients = async () => {
      try {
        const response = await taskApi.getClients();
        setClients(response.clients || []);
      } catch (error) {
        setClients([]);
      }
    };

    fetchModules();
    fetchClients();
  }, []);

  // Load workflows when client is selected
  useEffect(() => {
    if (selectedClient) {
      // Load workflows for selected client from backend
      const fetchWorkflows = async () => {
        try {
          const response = await workflowsAPI.getWorkflows(selectedClient);
          setWorkflows(response.data);
        } catch (error) {
          setWorkflows([]);
        }
      };
      fetchWorkflows();
    }
  }, [selectedClient]);

  // Update nodes with selection styling
  useEffect(() => {
    setNodes((nds) => 
      nds.map((node) => ({
        ...node,
        selected: node.id === selectedNodeId,
        style: {
          ...node.style,
          border: node.id === selectedNodeId ? '2px solid #1890ff' : '1px solid #d9d9d9',
          boxShadow: node.id === selectedNodeId ? '0 0 0 2px rgba(24, 144, 255, 0.2)' : 'none'
        }
      }))
    );
  }, [selectedNodeId, setNodes]);

  // Update edges with selection styling
  useEffect(() => {
    setEdges((eds) => 
      eds.map((edge) => ({
        ...edge,
        style: {
          ...edge.style,
          stroke: selectedEdges.includes(edge.id) ? '#ff8c00' : '#999',
          strokeWidth: selectedEdges.includes(edge.id) ? 3 : 2
        },
        markerEnd: {
          type: 'arrowclosed',
          color: selectedEdges.includes(edge.id) ? '#ff8c00' : '#999',
          width: 20,
          height: 20
        }
      }))
    );
  }, [selectedEdges, setEdges]);

  // Listen for inline note text updates
  useEffect(() => {
    const handleNoteTextUpdate = (event) => {
      const { nodeId, text } = event.detail;
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === nodeId && node.type === 'note') {
            return {
              ...node,
              data: {
                ...node.data,
                noteText: text
              }
            };
          }
          return node;
        })
      );
    };

    window.addEventListener('updateNoteText', handleNoteTextUpdate);
    return () => window.removeEventListener('updateNoteText', handleNoteTextUpdate);
  }, [setNodes]);

  const onConnect = useCallback(
    (params) => {
      // Only allow connections from source to target
      if (params.source && params.target && params.source !== params.target) {
        // Get source node to check if it's a branching module
        const sourceNode = nodes.find(n => n.id === params.source);
        const module = sourceNode?.data?.module;
        const moduleType = module?.type || module?.module_type || '';
        const moduleCategory = module?.category || '';
        const moduleName = module?.name || '';
        
        const isBranchingModule = (
          (moduleType === 'processing' || moduleType === 'utility') &&
          (moduleCategory === 'task_management' || moduleName.includes('Status Check'))
        );
        
        // Determine edge label based on sourceHandle
        let edgeLabel = '';
        let edgeStyle = { stroke: '#999', strokeWidth: 2 };
        
        if (isBranchingModule && params.sourceHandle) {
          if (params.sourceHandle === 'true') {
            edgeLabel = '✓ Pass';
            edgeStyle = { stroke: '#52c41a', strokeWidth: 2 };
          } else if (params.sourceHandle === 'false') {
            edgeLabel = '✗ Fail';
            edgeStyle = { stroke: '#ff4d4f', strokeWidth: 2 };
          }
        }
        
        setEdges((eds) => addEdge({
          ...params,
          id: `${params.source}-${params.target}-${params.sourceHandle || 'default'}`,
          label: edgeLabel,
          style: edgeStyle,
          type: 'bezier',
          markerEnd: {
            type: 'arrowclosed',
            color: edgeStyle.stroke,
            width: 20,
            height: 20
          },
          labelStyle: { fill: edgeStyle.stroke, fontWeight: 600, fontSize: 12 },
          labelBgStyle: { fill: '#fff', fillOpacity: 0.9 },
          labelBgPadding: [8, 4],
          labelBgBorderRadius: 4
        }, eds));
      }
    },
    [setEdges, nodes]
  );

  // Function to show node picker with only input nodes
  const showInputNodePicker = useCallback((position = { x: 400, y: 300 }) => {
    setNodePickerPosition(position); // Use same position for both screen and flow when manually opened
    setNodeDropPosition(position);
    setConnectionSource(null); // No connection source for initial node
    setShowOnlyInputNodes(true); // Show only input nodes
    setNodeSearchTerm(''); // Clear search term when opening
    setNodePickerVisible(true);
  }, []);

  // Close node picker
  const closeNodePicker = useCallback(() => {
    setNodePickerVisible(false);
    setConnectionSource(null);
    setShowOnlyInputNodes(false);
    setNodeSearchTerm(''); // Clear search term when closing
  }, []);

  // Close node picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (nodePickerVisible) {
        const picker = document.querySelector('.node-picker-overlay');
        if (picker && !picker.contains(event.target)) {
          closeNodePicker();
        }
      }
      
      // Close context menu when clicking outside
      if (contextMenuVisible) {
        const contextMenu = document.querySelector('.context-menu-overlay');
        if (contextMenu && !contextMenu.contains(event.target)) {
          setContextMenuVisible(false);
        }
      }
      
      // Close node context menu when clicking outside
      if (nodeContextMenuVisible) {
        const nodeContextMenu = document.querySelector('.node-context-menu-overlay');
        if (nodeContextMenu && !nodeContextMenu.contains(event.target)) {
          setNodeContextMenuVisible(false);
        }
      }
    };

    if (nodePickerVisible || contextMenuVisible || nodeContextMenuVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [nodePickerVisible, contextMenuVisible, nodeContextMenuVisible, closeNodePicker]);

  // Save workflow state to history
  const saveToHistory = useCallback((nodes, edges, action = 'change') => {
    if (isUndoRedoAction) return; // Don't save history during undo/redo

    const newHistoryItem = {
      nodes: JSON.parse(JSON.stringify(nodes)),
      edges: JSON.parse(JSON.stringify(edges)),
      timestamp: Date.now(),
      action
    };

    setHistory(prev => {
      // Remove any future history if we're not at the end
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(newHistoryItem);
      
      // Limit history to 50 items
      if (newHistory.length > 50) {
        newHistory.shift();
        // Don't increment index if we removed the first item
        setHistoryIndex(Math.min(newHistory.length - 1, 49));
        return newHistory;
      }
      
      // Set the index to the new last position
      setHistoryIndex(newHistory.length - 1);
      return newHistory;
    });
  }, [historyIndex, isUndoRedoAction]);

  // Undo functionality
  const undo = useCallback(() => {
    if (historyIndex > 0 && history.length > 0 && historyIndex < history.length) {
      const prevState = history[historyIndex - 1];
      
      if (prevState && prevState.nodes && prevState.edges) {
        setIsUndoRedoAction(true);
        
        // Clear any temporary connection state
        setConnectionSource(null);
        setNodePickerVisible(false);
        setShowOnlyInputNodes(false);
        setSelectedNodes([]);
        setSelectedEdges([]);
        
        // Restore nodes and edges
        setNodes(prevState.nodes);
        setEdges(prevState.edges);
        setHistoryIndex(prev => prev - 1);
        
        setTimeout(() => setIsUndoRedoAction(false), 100);
        message.success('Undone');
      }
    } else {
      // Fix invalid index
      if (historyIndex >= history.length && history.length > 0) {
        setHistoryIndex(history.length - 1);
      }
    }
  }, [historyIndex, history, setNodes, setEdges]);

  // Redo functionality
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1 && history.length > 0) {
      const nextState = history[historyIndex + 1];
      if (nextState && nextState.nodes && nextState.edges) {
        setIsUndoRedoAction(true);
        
        // Clear any temporary connection state
        setConnectionSource(null);
        setNodePickerVisible(false);
        setShowOnlyInputNodes(false);
        setSelectedNodes([]);
        setSelectedEdges([]);
        
        // Restore nodes and edges
        setNodes(nextState.nodes);
        setEdges(nextState.edges);
        setHistoryIndex(prev => prev + 1);
        
        setTimeout(() => setIsUndoRedoAction(false), 100);
        message.success('Redone');
      }
    }
  }, [historyIndex, history, setNodes, setEdges]);

  // Auto-layout nodes using Dagre
  const autoLayoutNodes = useCallback(() => {
    if (!nodes || nodes.length === 0) {
      message.info('No nodes to organize');
      return;
    }

    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({
      rankdir: 'TB',
      nodesep: 50,
      ranksep: 80,
      marginx: 20,
      marginy: 20,
    });

    const getNodeSize = (node) => {
      const measuredWidth = node?.measured?.width ?? node?.width ?? node?.__rf?.width;
      const measuredHeight = node?.measured?.height ?? node?.height ?? node?.__rf?.height;

      const fallback = node?.type === 'note'
        ? { width: 240, height: 160 }
        : { width: 180, height: 80 };

      return {
        width: Number.isFinite(measuredWidth) ? measuredWidth : fallback.width,
        height: Number.isFinite(measuredHeight) ? measuredHeight : fallback.height,
      };
    };

    nodes.forEach((node) => {
      const { width, height } = getNodeSize(node);
      dagreGraph.setNode(node.id, { width, height });
    });

    (edges || []).forEach((edge) => {
      if (edge?.source && edge?.target) {
        dagreGraph.setEdge(edge.source, edge.target);
      }
    });

    dagre.layout(dagreGraph);

    const laidOutNodes = nodes.map((node) => {
      const { width, height } = getNodeSize(node);
      const layoutedNode = dagreGraph.node(node.id);

      if (!layoutedNode) return node;

      return {
        ...node,
        position: {
          x: layoutedNode.x - width / 2,
          y: layoutedNode.y - height / 2,
        },
      };
    });

    setNodes(laidOutNodes);
    saveToHistory(laidOutNodes, edges, 'layout');
    message.success('Nodes organized');

    requestAnimationFrame(() => {
      reactFlowRef.current?.fitView?.();
    });
  }, [nodes, edges, setNodes, saveToHistory]);

  // Clear history when workflow changes
  const clearHistory = useCallback(() => {
    setHistory([]);
    setHistoryIndex(-1);
  }, []);

  // Copy node functionality
  const copyNode = useCallback((nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    if (node) {
      setCopiedNode(node);
      message.success('Node copied');
    }
  }, [nodes]);

  // Paste node functionality
  const pasteNode = useCallback(() => {
    if (!copiedNode) {
      message.warning('No node copied');
      return;
    }

    const newNode = {
      ...copiedNode,
      id: `${copiedNode.data.module.id}-${Date.now()}`,
      position: {
        x: copiedNode.position.x + 50, // Offset to avoid overlapping
        y: copiedNode.position.y + 50,
      },
      selected: false,
    };

    setNodes(nds => [...nds, newNode]);
    message.success('Node pasted');
  }, [copiedNode, setNodes]);

  // Save initial state to history when nodes/edges change
  useEffect(() => {
    // Always ensure we have an initial state in history
    if (history.length === 0) {
      // Save initial state (even if empty)
      saveToHistory(nodes, edges, 'initial');
    }
  }, [nodes, edges, history.length, saveToHistory]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle shortcuts when not in input fields
      if (event.target.tagName === 'INPUT' || 
          event.target.tagName === 'TEXTAREA' ||
          event.target.contentEditable === 'true') {
        return;
      }

      if (event.ctrlKey || event.metaKey) {
        if (event.key === 'z' && !event.shiftKey) {
          event.preventDefault();
          undo();
        } else if ((event.key === 'y') || (event.key === 'z' && event.shiftKey)) {
          event.preventDefault();
          redo();
        } else if (event.key === 'c' && selectedNodes.length > 0) {
          event.preventDefault();
          copyNode(selectedNodes[0]);
        } else if (event.key === 'v') {
          event.preventDefault();
          pasteNode();
        }
      } else if (event.key === 'Delete' || event.key === 'Backspace') {
        // Delete selected nodes and edges
        if (selectedNodes.length > 0 || selectedEdges.length > 0) {
          event.preventDefault();
          
          // Save current state before deletion
          saveToHistory(nodes, edges, 'before_delete');
          
          // eslint-disable-next-line no-unused-vars
          let deletedCount = 0;
          
          // Remove selected nodes
          if (selectedNodes.length > 0) {
            setNodes(nds => nds.filter(node => !selectedNodes.includes(node.id)));
            
            // Remove edges connected to deleted nodes
            setEdges(eds => eds.filter(edge => 
              !selectedNodes.includes(edge.source) && !selectedNodes.includes(edge.target)
            ));
            
            deletedCount += selectedNodes.length;
          }
          
          // Remove selected edges (only if no nodes were selected)
          if (selectedEdges.length > 0) {
            setEdges(eds => eds.filter(edge => !selectedEdges.includes(edge.id)));
            // eslint-disable-next-line no-unused-vars
            deletedCount += selectedEdges.length;
          }
          
          // Clear selections
          setSelectedNodes([]);
          setSelectedEdges([]);
          setSelectedNodeId(null);
          
          if (selectedNodes.length > 0 && selectedEdges.length > 0) {
            message.success(`Deleted ${selectedNodes.length} node(s) and ${selectedEdges.length} connection(s)`);
          } else if (selectedNodes.length > 0) {
            message.success(`Deleted ${selectedNodes.length} node(s)`);
          } else if (selectedEdges.length > 0) {
            message.success(`Deleted ${selectedEdges.length} connection(s)`);
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedNodes, selectedEdges, copyNode, pasteNode, undo, redo, nodes, edges, saveToHistory, setNodes, setEdges]);

  // Handle connection start (when user starts dragging from a handle)
  const onConnectStart = useCallback((event, { nodeId, handleType }) => {
    if (handleType === 'source') {
      setConnectionSource({ nodeId, handleType });
    }
  }, []);

  // Handle connection end (when user releases the drag)
  const onConnectEnd = useCallback((event) => {
    if (!connectionSource) return;

    // Check if the target is the pane (empty space) and not a node
    const targetIsPane = event.target.classList.contains('react-flow__pane');
    const targetIsNode = event.target.closest('.react-flow__node');
    
    // Only show node picker if dropped on empty space (pane) and not on a node
    if (targetIsPane && !targetIsNode) {
      // Get the position relative to the ReactFlow pane
      const reactFlowBounds = event.target.getBoundingClientRect();
      const screenPosition = {
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      };
      
      // Set screen position for picker display
      setNodePickerPosition(screenPosition);
      
      // Calculate flow position for node placement
      if (window.screenToFlowPosition) {
        const flowPosition = window.screenToFlowPosition(screenPosition);
        setNodeDropPosition(flowPosition);
      } else {
        // Fallback to using screen position directly
        const flowPosition = {
          x: screenPosition.x,
          y: screenPosition.y,
        };
        setNodeDropPosition(flowPosition);
      }
      
      setNodeSearchTerm(''); // Clear search term when opening
      setNodePickerVisible(true);
    }
    
    // Reset connection source after handling
    setConnectionSource(null);
  }, [connectionSource]);

  // Add a new node from the picker
  const addNodeFromPicker = useCallback((module) => {
    if (!nodeDropPosition) return;

    // Snap to grid using the flow position
    const snappedPosition = {
      x: Math.round(nodeDropPosition.x / 20) * 20,
      y: Math.round(nodeDropPosition.y / 20) * 20,
    };

    const newNode = {
      id: `${module.id}-${Date.now()}`,
      type: 'default',
      position: snappedPosition,
      data: {
        label: `${module.image || '📦'} ${module.name}`,
        module: module,
        settings: {},
        isInput: ['input', 'source'].includes(module?.type) || ['input', 'source'].includes(module?.module_type) || /input/i.test(module?.name || ''),
        displayName: module.name,
        icon: module.image || '📦'
      },
    };

    setNodes((nds) => {
      const newNodes = nds.concat(newNode);
      
      // Create connection from source to new node (only if connectionSource exists)
      if (connectionSource) {
        const newEdge = {
          id: `${connectionSource.nodeId}-${newNode.id}`,
          source: connectionSource.nodeId,
          target: newNode.id,
          style: { stroke: '#999', strokeWidth: 2 },
          type: 'bezier',
          markerEnd: {
            type: 'arrowclosed',
            color: '#999'
          }
        };

        setEdges((eds) => {
          const newEdges = [...eds, newEdge];
          
          // Save to history
          saveToHistory(newNodes, newEdges, 'add_node_with_connection');
          
          return newEdges;
        });
      } else {
        // Just adding a standalone node (no connection)
        saveToHistory(newNodes, edges, 'add_node');
      }
      
      return newNodes;
    });

    // Close picker and reset state
    setNodePickerVisible(false);
    setConnectionSource(null);
    setShowOnlyInputNodes(false);
  }, [nodeDropPosition, connectionSource, setNodes, setEdges, edges, saveToHistory]);

  // Add a new node from the context menu
  const addNodeFromContextMenu = useCallback((module) => {
    if (!contextMenuFlowPosition) return;

    // Snap to grid using the flow position
    const snappedPosition = {
      x: Math.round(contextMenuFlowPosition.x / 20) * 20,
      y: Math.round(contextMenuFlowPosition.y / 20) * 20,
    };

    const newNode = {
      id: `${module.id}-${Date.now()}`,
      type: 'default',
      position: snappedPosition,
      data: {
        label: `${module.image || '📦'} ${module.name}`,
        module: module,
        settings: {},
        isInput: ['input', 'source'].includes(module?.type) || ['input', 'source'].includes(module?.module_type) || /input/i.test(module?.name || ''),
        displayName: module.name,
        icon: module.image || '📦'
      },
    };

    setNodes((nds) => {
      const newNodes = nds.concat(newNode);
      saveToHistory(newNodes, edges, 'add_node_from_context');
      return newNodes;
    });

    // Close context menu
    setContextMenuVisible(false);
    
    message.success(`Added ${module.name} node`);
  }, [contextMenuFlowPosition, setNodes, edges, saveToHistory]);

  // Add a new note from the context menu
  const addNote = useCallback(() => {
    if (!contextMenuFlowPosition) return;

    // Snap to grid using the flow position
    const snappedPosition = {
      x: Math.round(contextMenuFlowPosition.x / 20) * 20,
      y: Math.round(contextMenuFlowPosition.y / 20) * 20,
    };

    const newNote = {
      id: `note-${Date.now()}`,
      type: 'note',
      position: snappedPosition,
      style: {
        width: 250,
        height: 150
      },
      data: {
        noteText: 'Click to edit this note',
        backgroundColor: '#fff9c4',
        textColor: '#333'
      },
      draggable: true,
      connectable: false,
      selectable: true,
      zIndex: -1
    };

    setNodes((nds) => {
      const newNodes = nds.concat(newNote);
      saveToHistory(newNodes, edges, 'add_note');
      return newNodes;
    });

    // Close context menu
    setContextMenuVisible(false);
    
    message.success('Note added');
  }, [contextMenuFlowPosition, setNodes, edges, saveToHistory]);

  // Show context menu node picker
  const showContextNodePicker = useCallback(() => {
    setNodePickerPosition(contextMenuPosition);
    setNodeDropPosition(contextMenuFlowPosition);
    setConnectionSource(null);
    setShowOnlyInputNodes(false);
    setNodeSearchTerm(''); // Clear search term when opening
    setNodePickerVisible(true);
    setContextMenuVisible(false);
  }, [contextMenuPosition, contextMenuFlowPosition]);

  // Update node to latest version
  const updateNodeToLatestVersion = useCallback(async (node) => {
    if (!node || !node.data.module) {
      message.error('Node module information not available');
      return null;
    }

    try {
      message.loading('Updating to latest version...', 0);
      
      // Get the latest modules from the database (already synced from engine)
      const response = await modulesAPI.getModules();
      const latestModules = response.data;
      
      // Find the latest version of this module by name
      const latestModule = latestModules.find(m => m.name === node.data.module.name);
      
      if (!latestModule) {
        message.destroy();
        message.error('Module not found in database');
        return null;
      }

      // Check if this is actually newer or different
      const currentModuleDate = new Date(node.data.module.created_at || 0);
      const latestModuleDate = new Date(latestModule.created_at || 0);
      
      // Compare module structures to see if there are any differences
      const currentFields = node.data.module.fields || [];
      const latestFields = latestModule.fields || [];
      
      const hasStructuralChanges = (
        currentFields.length !== latestFields.length ||
        node.data.module.description !== latestModule.description ||
        JSON.stringify(currentFields.sort((a, b) => a.name.localeCompare(b.name))) !== 
        JSON.stringify(latestFields.sort((a, b) => a.name.localeCompare(b.name)))
      );
      
      const hasTimestampUpdate = latestModuleDate > currentModuleDate;
      
      if (!hasStructuralChanges && !hasTimestampUpdate) {
        message.destroy();
        message.info('Node is already using the latest version (no changes detected)');
        return null;
      }

      // Prepare the updated node data
      const existingSettings = node.data.settings || {};
      const newSettings = {};
      
      // Only keep settings for fields that still exist in the new module
      latestModule.fields.forEach(field => {
        if (existingSettings.hasOwnProperty(field.name)) {
          newSettings[field.name] = existingSettings[field.name];
        } else if (field.default_value !== undefined) {
          newSettings[field.name] = field.default_value;
        }
      });

      // Create updated node data preserving all original properties
      const updatedNodeData = {
        ...node,
        data: {
          ...node.data,
          module: latestModule,
          settings: newSettings
        }
      };

      // Update the node with the latest module information
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id === node.id) {
            return updatedNodeData;
          }
          return n;
        })
      );

      // Also update the global modules list
      setModules(latestModules);

      message.destroy();
      message.success(`Node "${node.data.displayName || node.data.module.name}" updated to latest version`);
      setNodeContextMenuVisible(false);
      
      // Return the updated node data
      return updatedNodeData;
      
    } catch (error) {
      message.destroy();
      console.error('Error updating node to latest version:', error);
      message.error('Failed to update node to latest version');
      return null;
    }
  }, [setNodes, setModules]);

  // Check if a node needs updating
  const checkNodeNeedsUpdate = useCallback((nodeInfo) => {
    if (!nodeInfo || !nodeInfo.data || !nodeInfo.data.module || !modules || modules.length === 0) {
      return false;
    }

    const node = nodeInfo.data;
    
    // Find the latest version of this module by name
    const latestModule = modules.find(m => m.name === node.module.name);
    
    if (!latestModule) {
      return false;
    }

    // If the modules are the exact same object reference, no update needed
    if (node.module === latestModule) {
      return false;
    }

    // Compare module IDs - if they're different, there's likely an update
    if (node.module.id && latestModule.id && node.module.id !== latestModule.id) {
      // Debug for all modules to see what's happening
      console.log(`${node.module.name} ID comparison:`, {
        currentId: node.module.id,
        latestId: latestModule.id,
        currentCreatedAt: node.module.created_at,
        latestCreatedAt: latestModule.created_at,
        different: node.module.id !== latestModule.id
      });
      
      // For now, let's continue with other checks instead of immediately returning true
      // This will help us see if there are actual content differences
      // return true;
    }

    // Compare module structures to see if there are any differences
    const currentFields = node.module.fields || [];
    const latestFields = latestModule.fields || [];
    
    // Create a more robust comparison by sorting and comparing field structures
    const normalizeFields = (fields) => {
      return fields.map(field => ({
        name: field.name,
        type: field.type,
        required: field.required,
        default_value: field.default_value
      })).sort((a, b) => a.name.localeCompare(b.name));
    };

    const currentNormalizedFields = normalizeFields(currentFields);
    const latestNormalizedFields = normalizeFields(latestFields);

    const hasStructuralChanges = (
      currentFields.length !== latestFields.length ||
      node.module.description !== latestModule.description ||
      JSON.stringify(currentNormalizedFields) !== JSON.stringify(latestNormalizedFields)
    );
    
    // Handle timestamp comparison with proper date validation
    const currentCreatedAt = node.module.created_at;
    const latestCreatedAt = latestModule.created_at;
    
    let hasTimestampUpdate = false;
    
    // Only compare timestamps if both exist and are valid
    if (currentCreatedAt && latestCreatedAt) {
      const currentModuleDate = new Date(currentCreatedAt);
      const latestModuleDate = new Date(latestCreatedAt);
      
      // Check if dates are valid (not epoch time)
      if (currentModuleDate.getTime() > 0 && latestModuleDate.getTime() > 0) {
        hasTimestampUpdate = latestModuleDate > currentModuleDate;
      }
    }
    
    const needsUpdate = hasStructuralChanges || hasTimestampUpdate;
    
    // Debug logging for all nodes to understand why they're being marked as needing updates
    if (needsUpdate) {
      console.log(`${node.module.name} Update Analysis:`, {
        moduleId: node.module.id,
        latestId: latestModule.id,
        idsDifferent: node.module.id !== latestModule.id,
        hasStructuralChanges,
        hasTimestampUpdate,
        currentFieldsCount: (node.module.fields || []).length,
        latestFieldsCount: (latestModule.fields || []).length,
        currentCreatedAt: node.module.created_at,
        latestCreatedAt: latestModule.created_at,
        result: needsUpdate
      });
    }
    
    // Return true only if there are actual changes
    return needsUpdate;
  }, [modules]);

  // Update node and open configuration
  const updateNodeAndConfigure = useCallback(async (nodeInfo) => {
    try {
      console.log('updateNodeAndConfigure called with:', nodeInfo);
      
      // Find the complete node object from the nodes state
      const fullNode = nodes.find(n => n.id === nodeInfo.id);
      if (!fullNode) {
        console.error('Could not find node in nodes state');
        message.error('Node not found');
        return;
      }
      
      console.log('Updating and configuring node:', fullNode);
      
      // Track original fields before update
      const originalFields = fullNode.data?.module?.fields || [];
      const originalFieldNames = originalFields.map(field => field.name);
      
      // First update the node and get the updated node data
      const updatedNode = await updateNodeToLatestVersion(fullNode);
      
      if (!updatedNode) {
        console.log('Update failed or no update needed');
        return;
      }
      
      console.log('Update completed, opening modal with updated node:', updatedNode);
      
      // Use the returned updated node data to open the modal
      const module = updatedNode.data?.module;
      if (!module) {
        message.warning('No module data found for this node');
        return;
      }
      
      // Identify new fields that weren't in the original module
      const newFields = (module.fields || []).filter(field => 
        !originalFieldNames.includes(field.name)
      );
      const newFieldsNames = newFields.map(field => field.name);
      
      console.log('New fields detected:', newFieldsNames);
      setNewFieldNames(newFieldsNames);
      
      setEditingNode(updatedNode);
      setEditingModule(module);
      const currentSettings = updatedNode.data?.settings || {};
      const formData = {};
      if (module.fields) {
        module.fields.forEach(field => {
          formData[field.name] = currentSettings[field.name] || field.default_value || '';
        });
      }
      // Updated field names
      formData.displayName = updatedNode.data?.displayName || module.name;
      formData.icon = updatedNode.data?.icon || module.image || '📦';
      
      // Debug log to see what values are being set
      console.log('Setting form values (update and configure):', formData);
      
      nodeForm.setFieldsValue(formData);
      setIsNodeModalOpen(true);
      
    } catch (error) {
      console.error('Error updating and configuring node:', error);
      message.error('Failed to update and configure node');
    }
  }, [updateNodeToLatestVersion, nodeForm, nodes]);

  // Use ref to store latest values without causing re-renders
  const functionsRef = useRef({});
  functionsRef.current = {
    checkNodeNeedsUpdate,
    updateNodeAndConfigure,
    modules
  };

  // Create stable nodeTypes to prevent recreation on every render
  const nodeTypes = useMemo(() => ({
    default: (props) => (
      <CustomNode 
        {...props} 
        checkNodeNeedsUpdate={functionsRef.current.checkNodeNeedsUpdate}
        updateNodeAndConfigure={functionsRef.current.updateNodeAndConfigure}
        modules={functionsRef.current.modules}
      />
    ),
    note: (props) => <NoteNode {...props} />,
  }), []); // Empty dependency array - functions accessed via ref

  // Delete a specific node
  const deleteNode = useCallback((nodeId) => {
    // Save current state before deletion
    saveToHistory(nodes, edges, 'before_delete');
    
    // Remove the node
    setNodes(nds => nds.filter(node => node.id !== nodeId));
    
    // Remove edges connected to the deleted node
    setEdges(eds => eds.filter(edge => 
      edge.source !== nodeId && edge.target !== nodeId
    ));
    
    // Clear selections if this node was selected
    if (selectedNodes.includes(nodeId)) {
      setSelectedNodes(prev => prev.filter(id => id !== nodeId));
    }
    if (selectedNodeId === nodeId) {
      setSelectedNodeId(null);
    }
    
    message.success('Node deleted');
  }, [nodes, edges, saveToHistory, setNodes, setEdges, selectedNodes, selectedNodeId]);

  // Update recent workflows
  const updateRecentWorkflows = useCallback((workflow) => {
    setRecentWorkflows(prev => {
      // Find client info for this workflow
      const client = clients.find(c => c.id === workflow.client_id);
      
      // Create enhanced workflow object with additional metadata
      const enhancedWorkflow = {
        ...workflow,
        clientName: client?.name || 'Unknown Client',
        nodeCount: (workflow.nodes || []).length,
        lastOpened: new Date().toISOString(),
        preview: {
          hasNodes: (workflow.nodes || []).length > 0,
          nodeTypes: [...new Set((workflow.nodes || []).map(node => {
            const moduleType = node.data?.module?.type || node.data?.module?.module_type || '';
            return moduleType.toLowerCase();
          }).filter(Boolean))].slice(0, 3), // Show up to 3 unique node types
          connectionCount: (workflow.edges || []).length
        }
      };
      
      // Remove the workflow if it already exists in the list
      const filtered = prev.filter(w => w.id !== workflow.id);
      // Add to the beginning and keep only the last 3
      return [enhancedWorkflow, ...filtered].slice(0, 3);
    });
  }, [clients]);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const GRID_SIZE = 20; // grid size in px

  // Add onDrop handler back for drag and drop functionality
  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const moduleData = event.dataTransfer.getData('application/reactflow');
      if (typeof moduleData === 'undefined' || !moduleData) {
        return;
      }
      const module = JSON.parse(moduleData);
      const reactFlowWrapper = event.target.closest('.react-flow');
      if (!reactFlowWrapper) return;
      const reactFlowBounds = reactFlowWrapper.getBoundingClientRect();
      const screenPosition = {
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      };
      
      // Transform to flow coordinates
      let flowPosition;
      if (window.screenToFlowPosition) {
        flowPosition = window.screenToFlowPosition(screenPosition);
      } else {
        // Fallback to manual transformation using simple position
        flowPosition = {
          x: screenPosition.x,
          y: screenPosition.y,
        };
      }
      
      // snap to grid
      const snappedPosition = {
        x: Math.round(flowPosition.x / GRID_SIZE) * GRID_SIZE,
        y: Math.round(flowPosition.y / GRID_SIZE) * GRID_SIZE,
      };
      
      const newNode = {
        id: `${module.id}-${Date.now()}`,
        type: 'default',
        position: snappedPosition,
        data: {
          label: `${module.image || '📦'} ${module.name}`,
          module: module,
          settings: {},
          isInput: ['input', 'source'].includes(module?.type) || ['input', 'source'].includes(module?.module_type) || /input/i.test(module?.name || ''),
          // new overrides
          displayName: module.name,
          icon: module.image || '📦'
        },
      };
      
      setNodes((nds) => nds.concat(newNode));
    },
    [setNodes]
  );

  // eslint-disable-next-line no-unused-vars
  const onDragStart = (event, module) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify(module));
    event.dataTransfer.effectAllowed = 'move';
  };

  // Handle node double-click to open settings modal
  const onNodeDoubleClick = useCallback((event, node) => {
    // Prevent ReactFlow's default double-click behavior (zoom/center)
    event.preventDefault();
    event.stopPropagation();
    
    // Handle note nodes differently
    if (node.type === 'note') {
      setEditingNote(node);
      noteForm.setFieldsValue({
        noteText: node.data?.noteText || '',
        backgroundColor: node.data?.backgroundColor || '#fff9c4',
        textColor: node.data?.textColor || '#333',
        width: node.data?.width || 250,
        height: node.data?.height || 150
      });
      setIsNoteModalOpen(true);
      return;
    }
    
    const module = node.data?.module;
    if (!module) {
      message.warning('No module data found for this node');
      return;
    }
    
    // Clear new field highlighting for normal modal opening
    setNewFieldNames([]);
    
    setEditingNode(node);
    setEditingModule(module);
    const currentSettings = node.data?.settings || {};
    const formData = {};
    if (module.fields) {
      module.fields.forEach(field => {
        formData[field.name] = currentSettings[field.name] || field.default_value || '';
      });
    }
    // Updated field names
    formData.displayName = node.data?.displayName || module.name;
    formData.icon = node.data?.icon || module.image || '📦';
    
    // Debug log to see what values are being set
    console.log('Setting form values:', formData);
    
    nodeForm.setFieldsValue(formData);
    setIsNodeModalOpen(true);
  }, [nodeForm, noteForm]);

  // Handle node selection for copy/paste
  const onNodeClick = useCallback((event, node) => {
    // Prevent any default behaviors that might cause centering
    event.preventDefault();
    event.stopPropagation();
    
    // Only update selection, no viewport changes
    setSelectedNodeId(node.id);
    
    // Prevent any additional event propagation
    return false;
  }, []);

  // Handle pane clicks to prevent unwanted behavior
  const onPaneClick = useCallback((event) => {
    // Clear selection but don't allow any centering behavior
    event.preventDefault();
    setSelectedNodeId(null);
    setSelectedNodes([]);
    setSelectedEdges([]);
    
    // Close context menus if open
    setContextMenuVisible(false);
    setNodeContextMenuVisible(false);
  }, []);

  // Handle right-click on pane to show context menu
  const onPaneContextMenu = useCallback((event) => {
    event.preventDefault();
    
    // Get the position relative to the ReactFlow pane
    const reactFlowBounds = event.currentTarget.getBoundingClientRect();
    const screenPosition = {
      x: event.clientX - reactFlowBounds.left,
      y: event.clientY - reactFlowBounds.top,
    };
    
    // Set screen position for context menu display
    setContextMenuPosition({
      x: event.clientX,
      y: event.clientY
    });
    
    // Calculate flow position for node placement
    if (window.screenToFlowPosition) {
      const flowPosition = window.screenToFlowPosition(screenPosition);
      setContextMenuFlowPosition(flowPosition);
    } else {
      // Fallback to using screen position directly
      setContextMenuFlowPosition({
        x: screenPosition.x,
        y: screenPosition.y,
      });
    }
    
    setContextMenuVisible(true);
    setNodeContextMenuVisible(false); // Close node context menu if open
  }, []);

  // Handle right-click on node to show node context menu
  const onNodeContextMenu = useCallback((event, node) => {
    event.preventDefault();
    
    // Set node context menu position
    setNodeContextMenuPosition({
      x: event.clientX,
      y: event.clientY
    });
    
    setContextMenuNode(node);
    setNodeContextMenuVisible(true);
    setContextMenuVisible(false); // Close pane context menu if open
  }, []);

  // Handle selection changes for both nodes and edges
  const onSelectionChange = useCallback((params) => {
    // Update selected nodes
    const selectedNodeIds = params.nodes.map(node => node.id);
    setSelectedNodes(selectedNodeIds);
    
    // Update selected edges
    const selectedEdgeIds = params.edges.map(edge => edge.id);
    setSelectedEdges(selectedEdgeIds);
    
    // Update selectedNodeId for backward compatibility
    if (selectedNodeIds.length > 0) {
      setSelectedNodeId(selectedNodeIds[0]);
    } else {
      setSelectedNodeId(null);
    }
  }, []);

  // Update selected nodes when nodes change
  const onNodesChangeWithSelection = useCallback((changes) => {
    onNodesChange(changes);
    
    // Check if any nodes were removed and clear selection if needed
    changes.forEach(change => {
      if (change.type === 'remove' && change.id === selectedNodeId) {
        setSelectedNodeId(null);
      }
    });

    // Save to history for meaningful changes only (exclude select changes)
    const meaningfulChanges = changes.filter(change => 
      ['add', 'remove', 'position'].includes(change.type)
    );
    
    if (meaningfulChanges.length > 0 && !isUndoRedoAction) {
      // Check if this is a position change that's finished (not currently dragging)
      const positionChanges = meaningfulChanges.filter(change => change.type === 'position');
      const isPositionComplete = positionChanges.some(change => 
        change.type === 'position' && !change.dragging
      );
      
      // Check if this has non-position changes (add/remove)
      const hasStructuralChanges = meaningfulChanges.some(change => 
        ['add', 'remove'].includes(change.type)
      );
      
      if (hasStructuralChanges || isPositionComplete) {
        // For position changes, debounce to avoid spam during dragging
        if (isPositionComplete && !hasStructuralChanges) {
          // Clear existing timeout
          if (positionSaveTimeout.current) {
            clearTimeout(positionSaveTimeout.current);
          }
          
          // Set new timeout for position save
          positionSaveTimeout.current = setTimeout(() => {
            setNodes(currentNodes => {
              setEdges(currentEdges => {
                saveToHistory(currentNodes, currentEdges, 'node_position');
                return currentEdges;
              });
              return currentNodes;
            });
          }, 500); // Increased debounce to 500ms to reduce frequency
        } else {
          // For structural changes, save immediately
          setTimeout(() => {
            setNodes(currentNodes => {
              setEdges(currentEdges => {
                saveToHistory(currentNodes, currentEdges, 'node_change');
                return currentEdges;
              });
              return currentNodes;
            });
          }, 10);
        }
      }
    }
  }, [onNodesChange, selectedNodeId, isUndoRedoAction, saveToHistory, setNodes, setEdges]);

  // Update edges with history tracking
  const onEdgesChangeWithHistory = useCallback((changes) => {
    onEdgesChange(changes);
    
    // Save to history for certain change types
    const shouldSaveToHistory = changes.some(change => 
      ['add', 'remove'].includes(change.type)
    );
    
    if (shouldSaveToHistory && !isUndoRedoAction) {
      // Use setTimeout to ensure state is updated before saving
      setTimeout(() => {
        setNodes(currentNodes => {
          setEdges(currentEdges => {
            saveToHistory(currentNodes, currentEdges, 'edge_change');
            return currentEdges;
          });
          return currentNodes;
        });
      }, 0);
    }
  }, [onEdgesChange, isUndoRedoAction, saveToHistory, setNodes, setEdges]);

  // Save node settings
  const saveNodeSettings = async () => {
    try {
      const values = await nodeForm.validateFields();
      const { displayName, icon, ...fieldValues } = values;
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === editingNode.id) {
            const module = node.data.module;
            const finalName = displayName || node.data.displayName || module.name;
            const finalIcon = icon || node.data.icon || module.image || '📦';
            return {
              ...node,
              data: {
                ...node.data,
                settings: fieldValues,
                displayName: finalName,
                icon: finalIcon,
                label: `${finalIcon} ${finalName}`
              }
            };
          }
          return node;
        })
      );
      message.success('Node settings saved');
      setIsNodeModalOpen(false);
      setEditingNode(null);
      setEditingModule(null);
      setNewFieldNames([]); // Clear new field highlighting
      nodeForm.resetFields();
    } catch (error) {
      message.error('Please fill in all required fields');
    }
  };

  // Save note settings
  const saveNoteSettings = async () => {
    try {
      const values = await noteForm.validateFields();
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === editingNote.id) {
            return {
              ...node,
              data: {
                ...node.data,
                ...values
              }
            };
          }
          return node;
        })
      );
      message.success('Note saved');
      setIsNoteModalOpen(false);
      setEditingNote(null);
      noteForm.resetFields();
    } catch (error) {
      message.error('Please fill in all required fields');
    }
  };

  const saveWorkflow = async () => {
    if (!workflowName || !selectedClient) {
      message.error('Please enter workflow name and select a client');
      return;
    }

    try {
      const workflowData = {
        name: workflowName,
        client_id: selectedClient,
        nodes: nodes,
        edges: edges,
      };

      if (selectedWorkflow) {
        // Update existing workflow
        await workflowsAPI.updateWorkflow(selectedWorkflow, workflowData);
        message.success('Workflow updated successfully');
      } else {
        // Create new workflow
        await workflowsAPI.createWorkflow(workflowData);
        message.success('Workflow created successfully');
      }

      setIsModalOpen(false);
      setWorkflowName('');
      // Refresh workflows
      const response = await workflowsAPI.getWorkflows(selectedClient);
      setWorkflows(response.data);
    } catch (error) {
      message.error(selectedWorkflow ? 'Failed to update workflow' : 'Failed to create workflow');
    }
  };

  const saveCurrentWorkflow = async () => {
    if (!selectedWorkflow || !selectedClient) {
      message.error('No workflow selected to update');
      return;
    }

    try {
      const workflowData = {
        name: workflowName,
        client_id: selectedClient,
        nodes: nodes,
        edges: edges,
      };

      await workflowsAPI.updateWorkflow(selectedWorkflow, workflowData);
      message.success('Workflow updated successfully');
      
      // Refresh workflows
      const response = await workflowsAPI.getWorkflows(selectedClient);
      setWorkflows(response.data);
    } catch (error) {
      message.error('Failed to update workflow');
    }
  };

  const loadWorkflow = useCallback(async (workflow) => {
    try {
      // Fetch the latest workflow data from the backend
      const response = await workflowsAPI.getWorkflow(workflow.id);
      const latestWorkflow = response.data;
      
      // Use the latest data from backend instead of cached data
      setSelectedWorkflow(latestWorkflow.id);
      setNodes(latestWorkflow.nodes || []);
      
      // Ensure all edges use bezier type and preserve branch information
      const edgesWithBezier = (latestWorkflow.edges || []).map(edge => {
        // Preserve edge styling for branching modules
        let edgeStyle = { stroke: '#999', strokeWidth: 2 };
        let edgeLabel = edge.label || '';
        
        if (edge.sourceHandle === 'true') {
          edgeStyle = { stroke: '#52c41a', strokeWidth: 2 };
          if (!edgeLabel) edgeLabel = '✓ Pass';
        } else if (edge.sourceHandle === 'false') {
          edgeStyle = { stroke: '#ff4d4f', strokeWidth: 2 };
          if (!edgeLabel) edgeLabel = '✗ Fail';
        }
        
        return {
          ...edge,
          type: 'bezier',
          style: edgeStyle,
          label: edgeLabel,
          markerEnd: {
            type: 'arrowclosed',
            color: edgeStyle.stroke,
            width: 20,
            height: 20
          },
          labelStyle: edgeLabel ? { fill: edgeStyle.stroke, fontWeight: 600, fontSize: 12 } : undefined,
          labelBgStyle: edgeLabel ? { fill: '#fff', fillOpacity: 0.9 } : undefined,
          labelBgPadding: edgeLabel ? [8, 4] : undefined,
          labelBgBorderRadius: edgeLabel ? 4 : undefined
        };
      });
      setEdges(edgesWithBezier);
      
      setWorkflowName(latestWorkflow.name); // Set the workflow name for editing
      setShowOverview(false); // hide overview when a workflow is loaded
      setViewingVersion(null);
      setIsReadonlyVersion(false);
      
      // Set the selected client from the workflow's client_id to enable save button
      if (latestWorkflow.client_id && latestWorkflow.client_id !== selectedClient) {
        setSelectedClient(latestWorkflow.client_id);
      }
      
      // Update recent workflows with the latest data
      updateRecentWorkflows(latestWorkflow);
      
      // Update the main workflows list with the latest data
      setWorkflows(prevWorkflows => {
        return prevWorkflows.map(wf => 
          wf.id === latestWorkflow.id ? latestWorkflow : wf
        );
      });
      
      // Clear undo/redo history for new workflow
      clearHistory();
      
      // Fit the view to show all nodes after a short delay to ensure rendering is complete
      setTimeout(() => {
        if (reactFlowRef.current) {
          reactFlowRef.current.fitView();
        }
      }, 100);
      
      // Show success message
      message.success(`Loaded workflow: ${latestWorkflow.name} v${latestWorkflow.version || 1}`);
    } catch (error) {
      console.error('Failed to load workflow:', error);
      message.error('Failed to load workflow from backend');
      
      // Fallback to cached data if backend call fails
      setSelectedWorkflow(workflow.id);
      setNodes(workflow.nodes || []);
      
      // Ensure all edges use bezier type in fallback as well and preserve branch information
      const edgesWithBezier = (workflow.edges || []).map(edge => {
        // Preserve edge styling for branching modules
        let edgeStyle = { stroke: '#999', strokeWidth: 2 };
        let edgeLabel = edge.label || '';
        
        if (edge.sourceHandle === 'true') {
          edgeStyle = { stroke: '#52c41a', strokeWidth: 2 };
          if (!edgeLabel) edgeLabel = '✓ Pass';
        } else if (edge.sourceHandle === 'false') {
          edgeStyle = { stroke: '#ff4d4f', strokeWidth: 2 };
          if (!edgeLabel) edgeLabel = '✗ Fail';
        }
        
        return {
          ...edge,
          type: 'bezier',
          style: edgeStyle,
          label: edgeLabel,
          markerEnd: {
            type: 'arrowclosed',
            color: edgeStyle.stroke,
            width: 20,
            height: 20
          },
          labelStyle: edgeLabel ? { fill: edgeStyle.stroke, fontWeight: 600, fontSize: 12 } : undefined,
          labelBgStyle: edgeLabel ? { fill: '#fff', fillOpacity: 0.9 } : undefined,
          labelBgPadding: edgeLabel ? [8, 4] : undefined,
          labelBgBorderRadius: edgeLabel ? 4 : undefined
        };
      });
      setEdges(edgesWithBezier);
      
      setWorkflowName(workflow.name);
      setShowOverview(false);
      setViewingVersion(null);
      setIsReadonlyVersion(false);
      
      // Set the selected client from the workflow's client_id even in fallback
      if (workflow.client_id && workflow.client_id !== selectedClient) {
        setSelectedClient(workflow.client_id);
      }
      
      // Clear undo/redo history for new workflow
      clearHistory();
      
      // Fit the view to show all nodes after a short delay to ensure rendering is complete
      setTimeout(() => {
        if (reactFlowRef.current) {
          reactFlowRef.current.fitView();
        }
      }, 100);
      
      message.warning(`Loaded cached version of workflow: ${workflow.name}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearHistory, updateRecentWorkflows, selectedClient]);

  const duplicateWorkflow = async (workflow) => {
    try {
      await workflowsAPI.duplicateWorkflow(workflow.id);
      message.success('Workflow duplicated successfully');
      // Refresh workflows
      const response = await workflowsAPI.getWorkflows(selectedClient);
      setWorkflows(response.data);
    } catch (error) {
      message.error('Failed to duplicate workflow');
    }
  };

  const deleteWorkflow = async (workflowId) => {
    try {
      await workflowsAPI.deleteWorkflow(workflowId);
      if (selectedWorkflow === workflowId) {
        setSelectedWorkflow(null);
        setNodes([]);
        setEdges([]);
      }
      message.success('Workflow deleted successfully');
      // Refresh workflows
      const response = await workflowsAPI.getWorkflows(selectedClient);
      setWorkflows(response.data);
    } catch (error) {
      message.error('Failed to delete workflow');
    }
  };

  // Open rename modal
  const openRenameModal = (workflow) => {
    setRenameWorkflowId(workflow.id);
    setRenameWorkflowName(workflow.name);
    setIsRenameModalOpen(true);
  };

  // Commit rename
  const renameWorkflow = async () => {
    if (!renameWorkflowId || !renameWorkflowName.trim()) {
      message.error('Invalid workflow name');
      return;
    }
    try {
      const wf = workflows.find(w => w.id === renameWorkflowId);
      if (!wf) throw new Error('Workflow not found');
      await workflowsAPI.updateWorkflow(renameWorkflowId, {
        name: renameWorkflowName.trim(),
        client_id: wf.client_id || selectedClient,
        nodes: wf.nodes || [],
        edges: wf.edges || []
      });
      message.success('Workflow renamed');
      setIsRenameModalOpen(false);
      setRenameWorkflowId(null);
      setRenameWorkflowName('');
      // Refresh list
      const response = await workflowsAPI.getWorkflows(selectedClient || wf.client_id);
      setWorkflows(response.data);
      // If currently selected, update local name state
      if (selectedWorkflow === wf.id) {
        setWorkflowName(renameWorkflowName.trim());
      }
    } catch (e) {
      message.error('Failed to rename workflow');
    }
  };

  // Dynamic height for editor (fill viewport)
  // eslint-disable-next-line no-unused-vars
  const [viewportHeight, setViewportHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 900);
  useEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch versions when history modal opened
  const openHistory = async () => {
    if (!selectedWorkflow) return;
    try {
      const resp = await workflowsAPI.getWorkflowVersions(selectedWorkflow);
      setVersionHistory(resp.data);
      setIsHistoryModalOpen(true);
    } catch (e) {
      message.error('Failed to load version history');
    }
  };

  const viewVersion = async (version) => {
    try {
      const resp = await workflowsAPI.getWorkflowVersion(selectedWorkflow, version);
      const v = resp.data;
      setViewingVersion(v);
      setNodes(v.nodes || []);
      
      // Ensure all edges use bezier type
      const edgesWithBezier = (v.edges || []).map(edge => ({
        ...edge,
        type: 'bezier'
      }));
      setEdges(edgesWithBezier);
      
      setWorkflowName(v.name);
      setIsReadonlyVersion(true);
      setIsHistoryModalOpen(false);
      
      // Fit the view to show all nodes after a short delay to ensure rendering is complete
      setTimeout(() => {
        if (reactFlowRef.current) {
          reactFlowRef.current.fitView();
        }
      }, 100);
      
      message.info(`Viewing version ${v.version} (read-only)`);
    } catch (e) {
      message.error('Failed to load version');
    }
  };

  const exitReadonly = async () => {
    // reload latest workflow from list
    const wf = workflows.find(w => w.id === selectedWorkflow);
    if (wf) {
      loadWorkflow(wf);
      message.success('Returned to latest version editing');
    }
  };

  const saveAsNewVersion = async () => {
    if (!selectedWorkflow) return;
    try {
      await workflowsAPI.saveNewWorkflowVersion(selectedWorkflow, {
        name: workflowName,
        nodes,
        edges
      });
      message.success('Saved as new version');
      setIsReadonlyVersion(false);
      // refresh workflow list to get updated version number
      const response = await workflowsAPI.getWorkflows(selectedClient);
      setWorkflows(response.data);
      // reload newly updated workflow (find by id)
      const wf = response.data.find(w => w.id === selectedWorkflow);
      if (wf) loadWorkflow(wf);
    } catch (e) {
      message.error('Failed to save new version');
    }
  };

  const viewDiff = async (version) => {
    try {
      const resp = await workflowsAPI.getWorkflowVersionDiff(selectedWorkflow, version);
      setCurrentDiff(resp.data.diff);
      setDiffVersion(version);
      setIsDiffModalOpen(true);
    } catch (e) {
      message.error('Failed to load version diff');
    }
  };

  return (
    <div 
      className="workflow-editor-n9n" 
      style={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        background: '#f5f5f5',
        overflow: 'hidden'
      }}
    >


      {/* Main Content Area */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        
        {/* Workflow Canvas */}
        <div style={{ 
          flex: 1, 
          position: 'relative',
          background: '#fafafa',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Canvas Area */}
          <div style={{ flex: 1, position: 'relative' }}>
              <ReactFlowProvider>
                <WorkflowCanvas
                  ref={reactFlowRef}
                  nodes={nodes}
                  edges={edges}
                  onNodesChangeWithSelection={onNodesChangeWithSelection}
                  onEdgesChangeWithHistory={onEdgesChangeWithHistory}
                  onConnect={onConnect}
                  onConnectStart={onConnectStart}
                  onConnectEnd={onConnectEnd}
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  onNodeDoubleClick={onNodeDoubleClick}
                  onNodeClick={onNodeClick}
                  onNodeContextMenu={onNodeContextMenu}
                  onPaneClick={onPaneClick}
                  onPaneContextMenu={onPaneContextMenu}
                  onSelectionChange={onSelectionChange}
                  nodeTypes={nodeTypes}
                  snapGrid={snapGrid}
                  reactFlowStyle={reactFlowStyle}
                  connectionLineStyle={connectionLineStyle}
                  defaultEdgeOptions={defaultEdgeOptions}
                  controlsStyle={controlsStyle}
                  miniMapStyle={miniMapStyle}
                  nodeColor={nodeColor}
                />
              </ReactFlowProvider>

              {/* Floating Toolbar - Right Side */}
              {selectedWorkflow && (
                <div 
                  className="floating-toolbar"
                  style={{
                    position: 'absolute',
                    top: 20,
                    right: 20,
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: 8,
                    padding: 12,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    zIndex: 1000
                  }}
                >
                  {/* Copy indicator */}
                  {copiedNode && (
                    <>
                      <span style={{ fontSize: 11, color: '#666', fontStyle: 'italic' }}>
                        Node copied - Ctrl+V to paste
                      </span>
                      <Divider type="vertical" style={{ height: 20, margin: '0 4px' }} />
                    </>
                  )}
                  
                  {/* Read-only version buttons */}
                  {isReadonlyVersion && (
                    <>
                      <Button size="small" onClick={exitReadonly}>Exit Read-only</Button>
                      <Button size="small" onClick={saveAsNewVersion}>
                        Save As New Version
                      </Button>
                      <Divider type="vertical" style={{ height: 20, margin: '0 4px' }} />
                    </>
                  )}
                  
                  {/* Undo/Redo buttons */}
                  <Tooltip title="Undo (Ctrl+Z)">
                    <Button
                      size="small"
                      icon={<UndoOutlined />}
                      onClick={undo}
                      disabled={historyIndex <= 0}
                    />
                  </Tooltip>
                  <Tooltip title="Redo (Ctrl+Y)">
                    <Button
                      size="small"
                      icon={<RedoOutlined />}
                      onClick={redo}
                      disabled={historyIndex >= history.length - 1}
                    />
                  </Tooltip>

                  <Tooltip title="Organize nodes (Auto-layout)">
                    <Button
                      size="small"
                      icon={<ApartmentOutlined />}
                      onClick={autoLayoutNodes}
                      disabled={isReadonlyVersion}
                    />
                  </Tooltip>
                  
                  <Divider type="vertical" style={{ height: 20, margin: '0 4px' }} />
                  
                  {/* History button */}
                  <Tooltip title="View History">
                    <Button 
                      size="small" 
                      icon={<HistoryOutlined />} 
                      onClick={openHistory}
                    />
                  </Tooltip>
                  
                  <Divider type="vertical" style={{ height: 20, margin: '0 4px' }} />
                  
                  {/* Save button */}
                  <Tooltip title={selectedWorkflow ? 'Save Workflow (Ctrl+S)' : 'Save as New Workflow'}>
                    <Button
                      type="primary"
                      size="small"
                      icon={<SaveOutlined />}
                      onClick={selectedWorkflow ? saveCurrentWorkflow : () => setIsModalOpen(true)}
                      disabled={!selectedClient || isReadonlyVersion}
                    />
                  </Tooltip>
                </div>
              )}

              {/* Floating Toolbar - Left Side (Workflow Info) */}
              <div style={{
                position: 'absolute',
                top: 20,
                left: 20,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(8px)',
                borderRadius: 8,
                padding: 12,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                zIndex: 1000,
                maxWidth: 'calc(100vw - 500px)'
              }}>
                <Tooltip title="Open Workflows">
                  <Button 
                    type="text" 
                    icon={<MenuOutlined />} 
                    onClick={() => setSidebarVisible(true)}
                    style={{ 
                      fontSize: 16,
                      border: 'none',
                      background: 'transparent'
                    }}
                  />
                </Tooltip>
                
                <div style={{ 
                  fontSize: 16, 
                  fontWeight: 600, 
                  color: '#333',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 200
                }}>
                  {selectedWorkflow ? workflowName || 'Untitled Workflow' : 'Workflow Editor'}
                </div>
                
                {selectedWorkflow && (
                  <Tag color="blue" style={{ margin: 0 }}>
                    v{(() => {
                      const wf = workflows.find(w => w.id === selectedWorkflow);
                      return wf ? wf.version || 1 : 1;
                    })()}
                  </Tag>
                )}
                
                {isReadonlyVersion && (
                  <Tag color="orange" style={{ margin: 0 }}>Read-only</Tag>
                )}
              </div>

              {/* Empty workflow placeholder - show when workflow is selected but empty */}
              {selectedWorkflow && nodes.length === 0 && (
                <div 
                  className="workflow-empty-placeholder"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    color: '#999'
                  }}
                >
                  <div
                    className="workflow-empty-placeholder-circle"
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: '#f0f0f0',
                      border: '2px dashed #d9d9d9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 24,
                      color: '#bbb',
                      cursor: 'pointer',
                      margin: '0 auto 16px',
                      transition: 'all 0.2s ease',
                      ':hover': {
                        background: '#e6f7ff',
                        borderColor: '#1890ff',
                        color: '#1890ff'
                      }
                    }}
                    onClick={() => showInputNodePicker({ x: 400, y: 300 })}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#e6f7ff';
                      e.target.style.borderColor = '#1890ff';
                      e.target.style.color = '#1890ff';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#f0f0f0';
                      e.target.style.borderColor = '#d9d9d9';
                      e.target.style.color = '#bbb';
                    }}
                  >
                    <PlusOutlined />
                  </div>
                  <div style={{ color: '#999', fontSize: 16, marginBottom: 8 }}>
                    Start your workflow
                  </div>
                  <div style={{ color: '#bbb', fontSize: 14 }}>
                    Click to add your first input node
                  </div>
                </div>
              )}

              {/* Empty state when no workflow selected */}
              {!selectedWorkflow && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  color: '#999'
                }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>🔧</div>
                  <div style={{ fontSize: 18, marginBottom: 8 }}>No workflow selected</div>
                  <div style={{ fontSize: 14, marginBottom: 20 }}>Create a new workflow or select an existing one to get started</div>
                  
                  {/* Recent workflows */}
                  {recentWorkflows.length > 0 && (
                    <div style={{ 
                      marginBottom: 20,
                      background: '#fff',
                      borderRadius: 8,
                      padding: '16px 20px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #e8e8e8',
                      minWidth: 400,
                      maxWidth: 500
                    }}>
                      <div style={{ 
                        fontSize: 14, 
                        fontWeight: 600, 
                        marginBottom: 12,
                        color: '#333'
                      }}>
                        Recent Workflows
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {recentWorkflows.map((workflow, index) => (
                          <div
                            key={workflow.id}
                            onClick={() => loadWorkflow(workflow)}
                            style={{
                              padding: '12px 16px',
                              border: '1px solid #e8e8e8',
                              borderRadius: 8,
                              cursor: 'pointer',
                              background: '#fafafa',
                              transition: 'all 0.2s',
                              fontSize: 13
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#e6f7ff';
                              e.currentTarget.style.borderColor = '#1890ff';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(24, 144, 255, 0.2)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#fafafa';
                              e.currentTarget.style.borderColor = '#e8e8e8';
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            {/* Header with number, name and version */}
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              marginBottom: 8
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: 6,
                                  background: '#1890ff20',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 11,
                                  color: '#1890ff',
                                  fontWeight: 600,
                                  flexShrink: 0
                                }}>
                                  {index + 1}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ 
                                    fontWeight: 600, 
                                    fontSize: 14,
                                    color: '#333',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {workflow.name}
                                  </div>
                                  <div style={{ 
                                    fontSize: 12, 
                                    color: '#666',
                                    marginTop: 2
                                  }}>
                                    👤 {workflow.clientName || 'Unknown Client'}
                                  </div>
                                </div>
                              </div>
                              <Tag size="small" color="blue">
                                v{workflow.version || 1}
                              </Tag>
                            </div>
                            
                            {/* Preview section */}
                            {workflow.preview && (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                fontSize: 11,
                                color: '#888',
                                background: '#f8f9fa',
                                padding: '6px 8px',
                                borderRadius: 4,
                                border: '1px solid #f0f0f0'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <span>📦</span>
                                  <span>{workflow.nodeCount || 0} nodes</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <span>🔗</span>
                                  <span>{workflow.preview.connectionCount || 0} connections</span>
                                </div>
                                {workflow.preview.nodeTypes && workflow.preview.nodeTypes.length > 0 && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <span>⚡</span>
                                    <span>{workflow.preview.nodeTypes.join(', ')}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <Button 
                    type="primary" 
                    style={{ marginTop: 16 }}
                    onClick={() => setSidebarVisible(true)}
                  >
                    Open Workflows
                  </Button>
                </div>
              )}

              {/* Node Picker Overlay */}
              {nodePickerVisible && (
                <div
                  className="node-picker-overlay"
                  style={{
                    position: 'absolute',
                    left: nodePickerPosition.x - 150,
                    top: nodePickerPosition.y - 20,
                    width: 300,
                    maxHeight: 450,
                    background: '#fff',
                    border: '1px solid #e8e8e8',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    zIndex: 1000,
                    overflow: 'hidden'
                  }}
                >
                  <div style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #e8e8e8',
                    background: '#fafafa',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>
                      {showOnlyInputNodes ? 'Add Input Node' : 'Add Node'}
                    </span>
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={closeNodePicker}
                    />
                  </div>
                  <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0' }}>
                    <Input
                      placeholder="Search modules..."
                      value={nodeSearchTerm}
                      onChange={(e) => setNodeSearchTerm(e.target.value)}
                      style={{ fontSize: 12 }}
                      allowClear
                    />
                  </div>
                  <div style={{
                    maxHeight: 350,
                    overflow: 'auto',
                    padding: 8
                  }}>
                    {(() => {
                      const filteredModules = filteredAndSortedModules
                        .filter(module => {
                          // If showOnlyInputNodes is true, only show input/source modules
                          if (showOnlyInputNodes) {
                            const type = module.type || module.module_type || '';
                            return ['input', 'source'].includes(type.toLowerCase());
                          }
                          // Otherwise show all modules
                          return true;
                        });

                      if (filteredModules.length === 0) {
                        return (
                          <div style={{ 
                            padding: 20, 
                            textAlign: 'center', 
                            color: '#999', 
                            fontSize: 13 
                          }}>
                            {nodeSearchTerm.trim() ? 'No modules found matching your search' : 'No modules available'}
                          </div>
                        );
                      }

                      return filteredModules.map((module) => {
                        const getModuleColor = () => {
                          const type = module.type || module.module_type || '';
                          switch (type.toLowerCase()) {
                            case 'input':
                            case 'source':
                              return '#00d4aa';
                            case 'output':
                            case 'destination':
                              return '#ff6b6b';
                            case 'processor':
                            case 'transform':
                              return '#4dabf7';
                            case 'condition':
                            case 'logic':
                              return '#ffd43b';
                            case 'storage':
                              return '#9775fa';
                            default:
                              return '#868e96';
                          }
                        };

                        return (
                          <div
                            key={module.id}
                            onClick={() => addNodeFromPicker(module)}
                            style={{
                              padding: 12,
                              margin: '4px 0',
                              border: '1px solid #e8e8e8',
                              borderRadius: 6,
                              cursor: 'pointer',
                              background: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              transition: 'all 0.2s',
                              borderLeft: `4px solid ${getModuleColor()}`,
                              ':hover': {
                                background: '#f5f5f5'
                              }
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#f5f5f5';
                              e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = '#fff';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            <div style={{
                              width: 32,
                              height: 32,
                              borderRadius: 6,
                              background: `${getModuleColor()}20`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 14,
                              flexShrink: 0
                            }}>
                              {module.image || '📦'}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 500, fontSize: 13 }}>
                                {module.name}
                              </div>
                              <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase' }}>
                                {module.type || module.module_type || 'MODULE'}
                              </div>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}

              {/* Context Menu */}
              {contextMenuVisible && (
                <div
                  className="context-menu-overlay"
                  style={{
                    position: 'fixed',
                    left: contextMenuPosition.x,
                    top: contextMenuPosition.y,
                    minWidth: 200,
                    background: '#fff',
                    border: '1px solid #e8e8e8',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    zIndex: 1001,
                    overflow: 'hidden'
                  }}
                >
                  <div style={{
                    padding: '8px 0'
                  }}>
                    <div
                      onClick={showContextNodePicker}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        fontSize: 14,
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent';
                      }}
                    >
                      <PlusOutlined style={{ color: '#1890ff' }} />
                      <span>Add Node</span>
                    </div>
                    
                    <div
                      onClick={addNote}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        fontSize: 14,
                        transition: 'background 0.2s',
                        borderBottom: '1px solid #f0f0f0'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent';
                      }}
                    >
                      <span style={{ fontSize: 16 }}>📝</span>
                      <span>Add Note</span>
                    </div>
                    
                    {/* Quick add popular modules */}
                    <div style={{ padding: '4px 0' }}>
                      <div style={{ 
                        padding: '8px 16px', 
                        fontSize: 12, 
                        color: '#999', 
                        textTransform: 'uppercase',
                        fontWeight: 600
                      }}>
                        Quick Add
                      </div>
                      {filteredAndSortedModules
                        .filter(module => {
                          const type = module.type || module.module_type || '';
                          return ['input', 'output', 'processor'].includes(type.toLowerCase());
                        })
                        .slice(0, 4) // Show only first 4 popular modules
                        .map((module) => {
                          const getModuleColor = () => {
                            const type = module.type || module.module_type || '';
                            switch (type.toLowerCase()) {
                              case 'input':
                              case 'source':
                                return '#00d4aa';
                              case 'output':
                              case 'destination':
                                return '#ff6b6b';
                              case 'processor':
                              case 'transform':
                                return '#4dabf7';
                              default:
                                return '#868e96';
                            }
                          };

                          return (
                            <div
                              key={module.id}
                              onClick={() => addNodeFromContextMenu(module)}
                              style={{
                                padding: '10px 16px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                fontSize: 13,
                                transition: 'background 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = '#f5f5f5';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = 'transparent';
                              }}
                            >
                              <div style={{
                                width: 20,
                                height: 20,
                                borderRadius: 4,
                                background: `${getModuleColor()}20`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 10,
                                flexShrink: 0
                              }}>
                                {module.image || '📦'}
                              </div>
                              <span>{module.name}</span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              )}

              {/* Node Context Menu */}
              {nodeContextMenuVisible && contextMenuNode && (
                <div
                  className="node-context-menu-overlay"
                  style={{
                    position: 'fixed',
                    left: nodeContextMenuPosition.x,
                    top: nodeContextMenuPosition.y,
                    minWidth: 200,
                    background: '#fff',
                    border: '1px solid #e8e8e8',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    zIndex: 1001,
                    overflow: 'hidden'
                  }}
                >
                  <div style={{
                    padding: '8px 0'
                  }}>
                    <div style={{ 
                      padding: '8px 16px', 
                      fontSize: 12, 
                      color: '#999', 
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      borderBottom: '1px solid #f0f0f0'
                    }}>
                      {contextMenuNode.data?.displayName || contextMenuNode.data?.module?.name || 'Node'}
                    </div>
                    
                    <div
                      onClick={() => updateNodeToLatestVersion(contextMenuNode)}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        fontSize: 14,
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent';
                      }}
                    >
                      <HistoryOutlined style={{ color: '#52c41a' }} />
                      <span>Update to Latest Version</span>
                    </div>
                    
                    <div
                      onClick={() => {
                        // Clear new field highlighting for normal configuration
                        setNewFieldNames([]);
                        
                        setEditingNode(contextMenuNode);
                        setEditingModule(contextMenuNode.data.module);
                        
                        // Populate form with current settings
                        const module = contextMenuNode.data.module;
                        const currentSettings = contextMenuNode.data?.settings || {};
                        const formData = {};
                        if (module.fields) {
                          module.fields.forEach(field => {
                            formData[field.name] = currentSettings[field.name] || field.default_value || '';
                          });
                        }
                        // Updated field names
                        formData.displayName = contextMenuNode.data?.displayName || module.name;
                        formData.icon = contextMenuNode.data?.icon || module.image || '📦';
                        nodeForm.setFieldsValue(formData);
                        
                        setIsNodeModalOpen(true);
                        setNodeContextMenuVisible(false);
                      }}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        fontSize: 14,
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent';
                      }}
                    >
                      <SettingOutlined style={{ color: '#1890ff' }} />
                      <span>Configure</span>
                    </div>
                    
                    <div
                      onClick={() => {
                        copyNode(contextMenuNode.id);
                        setNodeContextMenuVisible(false);
                      }}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        fontSize: 14,
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent';
                      }}
                    >
                      <CopyOutlined style={{ color: '#722ed1' }} />
                      <span>Duplicate</span>
                    </div>
                    
                    <div
                      onClick={() => {
                        deleteNode(contextMenuNode.id);
                        setNodeContextMenuVisible(false);
                      }}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        fontSize: 14,
                        transition: 'background 0.2s',
                        borderTop: '1px solid #f0f0f0'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#fff2f0';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent';
                      }}
                    >
                      <DeleteOutlined style={{ color: '#ff4d4f' }} />
                      <span style={{ color: '#ff4d4f' }}>Delete</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
        </div>
      </div>

      {/* Sidebar Drawer */}
      <Drawer
        title="Workflows"
        placement="left"
        onClose={() => setSidebarVisible(false)}
        open={sidebarVisible}
        width={400}
        bodyStyle={{ padding: 16 }}
      >
        {/* Client Selection */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>Client</div>
          <Select
            style={{ width: '100%' }}
            placeholder="Select a client"
            value={selectedClient}
            onChange={(val) => { 
              setSelectedClient(val); 
              setSelectedWorkflow(null); 
            }}
          >
            {clients.map(client => (
              <Option key={client.id} value={client.id}>
                {client.name}
              </Option>
            ))}
          </Select>
        </div>

        {/* Workflows Section */}
        {selectedClient && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: 12 
            }}>
              <div style={{ fontWeight: 600 }}>Workflows</div>
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => {
                  setSelectedWorkflow(null);
                  setNodes([]);
                  setEdges([]);
                  setWorkflowName('');
                  setIsModalOpen(true);
                }}
              >
                New
              </Button>
            </div>
            
            <div style={{ maxHeight: '100vh', overflow: 'auto' }}>
              {workflows.map((workflow) => (
                <div
                  key={workflow.id}
                  style={{
                    padding: 12,
                    border: selectedWorkflow === workflow.id ? '2px solid #1890ff' : '1px solid #e8e8e8',
                    borderRadius: 6,
                    marginBottom: 8,
                    cursor: 'pointer',
                    background: selectedWorkflow === workflow.id ? '#f0f8ff' : '#fff',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => {
                    loadWorkflow(workflow);
                    setSidebarVisible(false);
                  }}
                >
                  <div style={{ fontWeight: 500, marginBottom: 4 }}>
                    {workflow.name}
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    v{workflow.version || 1}
                  </div>
                  <div style={{ 
                    marginTop: 8, 
                    display: 'flex', 
                    gap: 4,
                    onClick: (e) => e.stopPropagation()
                  }}>
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        openRenameModal(workflow);
                      }}
                    />
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        duplicateWorkflow(workflow);
                      }}
                    />
                    <Popconfirm
                      title="Delete this workflow?"
                      description="This will permanently delete the workflow."
                      onConfirm={() => deleteWorkflow(workflow.id)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button size="small" icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      
      </Drawer>

      {/* Modals */}
      <Modal
        title={selectedWorkflow ? "Update Workflow" : "Save New Workflow"}
        open={isModalOpen}
        onOk={saveWorkflow}
        onCancel={() => {
          setIsModalOpen(false);
          if (!selectedWorkflow) {
            setWorkflowName(''); // Only clear name if creating new workflow
          }
        }}
        okText={selectedWorkflow ? "Update" : "Save"}
      >
        <Input
          placeholder="Enter workflow name"
          value={workflowName}
          onChange={(e) => setWorkflowName(e.target.value)}
          suffix={
            <TemplateVariableButton 
              inputRef={null}
              onInsert={(newValue) => {
                setWorkflowName(workflowName + newValue);
              }}
            />
          }
        />
      </Modal>

      {/* Node Settings Modal */}
      <Modal
        key={editingModule?.id || 'node-modal'} // Force re-render when module changes
        title={`Configure ${editingModule?.name || 'Node'}`}
        open={isNodeModalOpen}
        onOk={saveNodeSettings}
        onCancel={() => {
          setIsNodeModalOpen(false);
          setEditingNode(null);
          setEditingModule(null);
          setNewFieldNames([]); // Clear new field highlighting
          nodeForm.resetFields();
        }}
        okText="Save Settings"
        width="80vw"
      >
        {editingModule && (
          <Form
            form={nodeForm}
            layout="vertical"
          >
            {/* Debug section - temporary */}
            <div style={{ 
              backgroundColor: '#f0f0f0', 
              padding: '10px', 
              marginBottom: '10px', 
              fontSize: '12px',
              borderRadius: '4px',
              display: 'none'
            }}>
            </div>
            
            {/* Renamed: Editable node icon & display name */}
            <Form.Item
              name="icon"
              label="Icon"
              tooltip="Emoji or short icon string"
              rules={[{ required: false }]}
            >
              <Input 
                placeholder="e.g. 📦" 
                maxLength={4} 
                style={{ width: 120 }} 
                onFocus={() => { lastFocusedNodeFieldRef.current = 'icon'; }}
                suffix={
                  <TemplateVariableButton 
                    inputRef={null}
                    onBrowseNodeContext={openNodeContextBrowser}
                    onInsert={(newValue) => {
                      const currentValue = nodeForm.getFieldValue('icon') || '';
                      nodeForm.setFieldsValue({ icon: currentValue + newValue });
                    }}
                  />
                }
              />
            </Form.Item>
            <Form.Item
              name="displayName"
              label="Display Name"
              rules={[{ required: true, message: 'Display Name is required' }]}
            >
              <Input 
                placeholder="Enter node display name" 
                onFocus={() => { lastFocusedNodeFieldRef.current = 'displayName'; }}
                suffix={
                  <TemplateVariableButton 
                    inputRef={null}
                    onBrowseNodeContext={openNodeContextBrowser}
                    onInsert={(newValue) => {
                      const currentValue = nodeForm.getFieldValue('displayName') || '';
                      nodeForm.setFieldsValue({ displayName: currentValue + newValue });
                    }}
                  />
                }
              />
            </Form.Item>
            {editingModule.fields?.map((field, index) => {
              const isNewField = newFieldNames.includes(field.name);
              
              // Check if field should be visible based on conditional logic
              const shouldShowField = () => {
                if (!field.conditional) {
                  return true; // No condition, always show
                }
                
                const { field: conditionField, value: conditionValue, operator } = field.conditional;
                
                // Get the current value, trying multiple sources
                let currentFormValue = formValues?.[conditionField];
                if (currentFormValue === undefined) {
                  currentFormValue = nodeForm.getFieldValue(conditionField);
                }
                
                // If still undefined, check if there's a default value from the condition field definition
                if (currentFormValue === undefined) {
                  const conditionFieldDef = editingModule.fields?.find(f => f.name === conditionField);
                  if (conditionFieldDef?.default_value !== undefined) {
                    currentFormValue = conditionFieldDef.default_value;
                  }
                }
                
                // Convert values to strings for comparison to avoid type issues
                const currentValueStr = String(currentFormValue || '');
                const expectedValueStr = String(conditionValue || '');
                
                // Debug log to see what's happening
                console.log(`Field ${field.name} conditional check:`, {
                  conditionField,
                  conditionValue: expectedValueStr,
                  currentFormValue: currentValueStr,
                  rawCurrentValue: currentFormValue,
                  rawExpectedValue: conditionValue,
                  formValues: formValues?.[conditionField],
                  nodeFormValue: nodeForm.getFieldValue(conditionField),
                  shouldShow: currentValueStr === expectedValueStr
                });
                
                // Handle different operators (default to equality if not specified)
                switch (operator) {
                  case '!=':
                  case 'not_equals':
                    return currentValueStr !== expectedValueStr;
                  case 'in':
                    return Array.isArray(conditionValue) && conditionValue.includes(currentFormValue);
                  case 'not_in':
                    return Array.isArray(conditionValue) && !conditionValue.includes(currentFormValue);
                  default:
                    // Default equality check with string comparison
                    return currentValueStr === expectedValueStr;
                }
              };
              
              if (!shouldShowField()) {
                return null; // Hide field if condition not met
              }
              
              // Special compact rendering for Context Manager regex fields
              if (editingModule.name === 'Context Manager') {
                // Skip individual field rendering for all regex-related fields
                if (['regex_source_key', 'regex_pattern', 'regex_replacement', 'regex_target_key', 'regex_flags'].includes(field.name)) {
                  return null;
                }
                
                // Skip enable_regex field - it's handled by operation_mode
                if (field.name === 'enable_regex') {
                  return null;
                }
                
                // Skip context_data when in regex mode (conditional should handle this, but double-check)
                if (field.name === 'context_data') {
                  const operationMode = formValues?.operation_mode ?? nodeForm.getFieldValue('operation_mode') ?? 'set_context';
                  if (operationMode !== 'set_context') {
                    return null;
                  }
                }
                
                // Render compact component when we hit operation_mode field and regex_transform is selected
                if (field.name === 'operation_mode') {
                  const operationMode = formValues?.operation_mode ?? nodeForm.getFieldValue('operation_mode') ?? 'set_context';
                  const showRegex = operationMode === 'regex_transform';
                
                  // Render the operation_mode select
                  return (
                    <div key="operation-mode-section">
                      <Form.Item
                        name="operation_mode"
                        label="Operation Mode"
                        rules={[{ required: true, message: 'Operation Mode is required' }]}
                        style={{
                          marginBottom: 8
                        }}
                      >
                        <Select placeholder="Select operation mode">
                          <Option value="set_context">Set Context Data</Option>
                          <Option value="regex_transform">Regex Transform</Option>
                        </Select>
                      </Form.Item>
                      
                      {showRegex && (
                        <Form.Item
                          label={
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              Regex Context Transform
                              <span style={{
                                backgroundColor: '#52c41a',
                                color: 'white',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                padding: '2px 6px',
                                borderRadius: '8px',
                                textTransform: 'uppercase'
                              }}>
                                NEW
                              </span>
                            </div>
                          }
                          style={{
                            backgroundColor: '#f6ffed',
                            padding: '12px',
                            borderRadius: '6px',
                            border: '1px solid #b7eb8f',
                            marginBottom: '16px'
                          }}
                        >
                          <RegexContextTransform
                            sourceKey={nodeForm.getFieldValue('regex_source_key') || ''}
                            pattern={nodeForm.getFieldValue('regex_pattern') || ''}
                            replacement={nodeForm.getFieldValue('regex_replacement') || ''}
                            targetKey={nodeForm.getFieldValue('regex_target_key') || ''}
                            flags={nodeForm.getFieldValue('regex_flags') || []}
                            onSourceKeyChange={(value) => nodeForm.setFieldsValue({ regex_source_key: value })}
                            onPatternChange={(value) => nodeForm.setFieldsValue({ regex_pattern: value })}
                            onReplacementChange={(value) => nodeForm.setFieldsValue({ regex_replacement: value })}
                            onTargetKeyChange={(value) => nodeForm.setFieldsValue({ regex_target_key: value })}
                          />
                          
                          {/* Hidden fields to maintain form state */}
                          <div style={{ display: 'none' }}>
                            <Form.Item name="regex_source_key"><Input /></Form.Item>
                            <Form.Item name="regex_pattern"><Input /></Form.Item>
                            <Form.Item name="regex_replacement"><Input /></Form.Item>
                            <Form.Item name="regex_target_key"><Input /></Form.Item>
                            <Form.Item name="regex_flags">
                              <Select mode="multiple">
                                <Option value="IGNORECASE">Ignore Case</Option>
                                <Option value="MULTILINE">Multiline</Option>
                                <Option value="DOTALL">Dot All</Option>
                              </Select>
                            </Form.Item>
                          </div>
                      </Form.Item>
                    )}
                  </div>
                  );
                }
              }
              
              return (
                <Form.Item
                  key={`${field.name}-${field.id || index}-${formValues?.[field.conditional?.field] || 'no-condition'}`}
                  name={field.name}
                  label={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {field.label}
                      {isNewField && (
                        <span style={{
                          backgroundColor: '#52c41a',
                          color: 'white',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          padding: '2px 6px',
                          borderRadius: '8px',
                          textTransform: 'uppercase'
                        }}>
                          NEW
                        </span>
                      )}
                    </div>
                  }
                  rules={[
                    { required: field.required, message: `${field.label} is required` }
                  ]}
                  style={isNewField ? {
                    backgroundColor: '#f6ffed',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid #b7eb8f',
                    marginBottom: '16px'
                  } : undefined}
                >
                {/* Special handling for Filename Pattern fields with regex testing */}
                {(field.label === 'Filename Pattern' || 
                  field.name === 'filename_pattern' || 
                  field.name === 'pattern' || 
                  (field.label && field.label.toLowerCase().includes('pattern')) ||
                  (field.label && field.label.toLowerCase().includes('regex'))) ? (
                  <RegexTester
                    value={nodeForm.getFieldValue(field.name) || field.default_value || ''}
                    onChange={(value) => nodeForm.setFieldsValue({ [field.name]: value })}
                    placeholder={typeof field.label === 'string' ? `Enter ${field.label.toLowerCase()}` : 'Enter regex pattern'}
                  />
                ) : field.field_type === 'string' ? (
                  <Input 
                    placeholder={(typeof field.label === 'string' ? `Enter ${field.label.toLowerCase()}` : 'Enter value')}
                    onFocus={() => { lastFocusedNodeFieldRef.current = field.name; }}
                    suffix={
                      <TemplateVariableButton 
                        inputRef={null}
                        onBrowseNodeContext={openNodeContextBrowser}
                        onInsert={(newValue) => {
                          const currentValue = nodeForm.getFieldValue(field.name) || '';
                          nodeForm.setFieldsValue({ [field.name]: currentValue + newValue });
                        }}
                      />
                    }
                  />
                ) : field.field_type === 'number' ? (
                  <Input 
                    type="number"
                    placeholder={(typeof field.label === 'string' ? `Enter ${field.label.toLowerCase()}` : 'Enter number')}
                    onFocus={() => { lastFocusedNodeFieldRef.current = field.name; }}
                    suffix={
                      <TemplateVariableButton 
                        inputRef={null}
                        onBrowseNodeContext={openNodeContextBrowser}
                        onInsert={(newValue) => {
                          const currentValue = nodeForm.getFieldValue(field.name) || '';
                          nodeForm.setFieldsValue({ [field.name]: currentValue + newValue });
                        }}
                      />
                    }
                  />
                ) : field.field_type === 'integer' ? (
                  <Input 
                    type="number"
                    placeholder={(typeof field.label === 'string' ? `Enter ${field.label.toLowerCase()}` : 'Enter integer')}
                    onFocus={() => { lastFocusedNodeFieldRef.current = field.name; }}
                    suffix={
                      <TemplateVariableButton 
                        inputRef={null}
                        onBrowseNodeContext={openNodeContextBrowser}
                        onInsert={(newValue) => {
                          const currentValue = nodeForm.getFieldValue(field.name) || '';
                          nodeForm.setFieldsValue({ [field.name]: currentValue + newValue });
                        }}
                      />
                    }
                  />
                ) : field.field_type === 'boolean' ? (
                  <Select placeholder={(typeof field.label === 'string' ? `Select ${field.label.toLowerCase()}` : 'Select option')}>
                    <Option value={true}>Yes</Option>
                    <Option value={false}>No</Option>
                  </Select>
                ) : field.field_type === 'textarea' ? (
                  <div style={{ position: 'relative' }}>
                    <Input.TextArea 
                      rows={4}
                      placeholder={(typeof field.label === 'string' ? `Enter ${field.label.toLowerCase()}` : 'Enter text')}
                      onFocus={() => { lastFocusedNodeFieldRef.current = field.name; }}
                    />
                    <div style={{ 
                      position: 'absolute', 
                      top: '8px', 
                      right: '8px', 
                      zIndex: 1,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      borderRadius: '4px',
                      padding: '2px'
                    }}>
                      <TemplateVariableButton 
                        inputRef={null}
                        onBrowseNodeContext={openNodeContextBrowser}
                        onInsert={(newValue) => {
                          const currentValue = nodeForm.getFieldValue(field.name) || '';
                          nodeForm.setFieldsValue({ [field.name]: currentValue + newValue });
                        }}
                      />
                    </div>
                  </div>
                ) : field.field_type === 'code' ? (
                  <div>
                    <CodeEditor
                      value={nodeForm.getFieldValue(field.name) || field.default_value || ''}
                      onChange={(value) => nodeForm.setFieldsValue({ [field.name]: value })}
                      language="python"
                      placeholder={field.placeholder || '# Enter your Python code here\n# Access context via \'context\' variable\n# Set results via \'result\' variable'}
                      height="400px"
                    />
                    <div style={{ 
                      marginTop: '8px', 
                      padding: '8px 12px',
                      background: '#f0f5ff',
                      border: '1px solid #adc6ff',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#0050b3'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>💡 Quick Guide:</div>
                      <div>• <code style={{ background: '#fff', padding: '2px 6px', borderRadius: '3px' }}>context</code> - Access input data from previous steps (e.g., <code style={{ background: '#fff', padding: '2px 6px', borderRadius: '3px' }}>context.get('input_data')</code>)</div>
                      <div>• <code style={{ background: '#fff', padding: '2px 6px', borderRadius: '3px' }}>result</code> - Dictionary to set output for next steps (e.g., <code style={{ background: '#fff', padding: '2px 6px', borderRadius: '3px' }}>result = {'{\'output\': value}'}</code>)</div>
                      <div style={{ marginTop: '4px', fontStyle: 'italic' }}>→ Next steps access your result via <code style={{ background: '#fff', padding: '2px 6px', borderRadius: '3px' }}>trigger_data['result']['your_key']</code></div>
                    </div>
                  </div>
                ) : field.field_type === 'key_value' ? (
                  <KeyValueEditor
                    value={nodeForm.getFieldValue(field.name) || field.default_value || ''}
                    onChange={(value) => nodeForm.setFieldsValue({ [field.name]: value })}
                    placeholder={field.placeholder || "key1: value1\nkey2: value2\nkey3: {\"nested\": \"object\"}"}
                    onKeyPicked={(token) => {
                      insertIntoLastFocusedNodeField(token);
                    }}
                  />
                ) : field.field_type === 'regex_transform_list' ? (
                  <>
                    {console.log('Rendering regex_transform_list field:', field.name, nodeForm.getFieldValue(field.name))}
                    <RegexTransformList
                      value={nodeForm.getFieldValue(field.name) || field.default_value || []}
                      onChange={(value) => {
                        console.log('RegexTransformList onChange called with:', value);
                        nodeForm.setFieldsValue({ [field.name]: value });
                      }}
                    />
                  </>
                ) : field.field_type === 'context_group_list' ? (
                  <>
                    {console.log('Rendering context_group_list:', field.name, 'group_schema:', field.group_schema)}
                    <ContextGroupList
                      value={nodeForm.getFieldValue(field.name) || field.default_value || []}
                      onChange={(value) => {
                        nodeForm.setFieldsValue({ [field.name]: value });
                      }}
                      groupSchema={field.group_schema || (field.options && field.options.group_schema) || []}
                    />
                  </>
                ) : field.field_type === 'select' && field.options ? (
                  <Select placeholder={(typeof field.label === 'string' ? `Select ${field.label.toLowerCase()}` : 'Select option')}>
                    {Array.isArray(field.options)
                      ? field.options.map((option) => (
                          typeof option === 'object' && option.value && option.label 
                            ? <Option key={option.value} value={option.value}>{option.label}</Option>
                            : <Option key={option} value={option}>{option}</Option>
                        ))
                      : Object.entries(field.options).map(([key, label]) => (
                          <Option key={key} value={key}>{label}</Option>
                        ))
                    }
                  </Select>
                ) : field.field_type === 'multiselect' && field.options ? (
                  <Select 
                    mode="multiple"
                    placeholder={(typeof field.label === 'string' ? `Select ${field.label.toLowerCase()}` : 'Select options')}
                  >
                    {Array.isArray(field.options)
                      ? field.options.map((option) => (
                          typeof option === 'object' && option.value && option.label 
                            ? <Option key={option.value} value={option.value}>{option.label}</Option>
                            : <Option key={option} value={option}>{option}</Option>
                        ))
                      : Object.entries(field.options).map(([key, label]) => (
                          <Option key={key} value={key}>{label}</Option>
                        ))
                    }
                  </Select>
                ) : field.field_type === 'storage_select' ? (
                  <StorageConfigSelector
                    value={nodeForm.getFieldValue(field.name)}
                    onChange={(value) => nodeForm.setFieldsValue({ [field.name]: value })}
                  />
                ) : field.field_type === 'status_select' ? (
                  <StatusSelector
                    value={nodeForm.getFieldValue(field.name)}
                    onChange={(value) => nodeForm.setFieldsValue({ [field.name]: value })}
                  />
                ) : (
                  <Input 
                    placeholder={(typeof field.label === 'string' ? `Enter ${field.label.toLowerCase()}` : 'Enter value')}
                    onFocus={() => { lastFocusedNodeFieldRef.current = field.name; }}
                    suffix={
                      <TemplateVariableButton 
                        inputRef={null}
                        onBrowseNodeContext={openNodeContextBrowser}
                        onInsert={(newValue) => {
                          const currentValue = nodeForm.getFieldValue(field.name) || '';
                          nodeForm.setFieldsValue({ [field.name]: currentValue + newValue });
                        }}
                      />
                    }
                  />
                )}
              </Form.Item>
              );
            })}
            {(!editingModule.fields || editingModule.fields.length === 0) && (
              <p>No configurable fields for this module.</p>
            )}
          </Form>
        )}
      </Modal>

      {/* Node Context Browser Modal */}
      <Modal
        title="Node Context Browser"
        open={isNodeContextBrowserOpen}
        onCancel={() => setIsNodeContextBrowserOpen(false)}
        footer={null}
        width={700}
      >
        <Input
          placeholder="Search context keys/values"
          value={nodeContextBrowserSearch}
          onChange={(e) => setNodeContextBrowserSearch(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <List
          size="small"
          bordered
          dataSource={filteredNodeContextBrowserEntries}
          locale={{ emptyText: 'No context keys match your search' }}
          style={{ maxHeight: 420, overflowY: 'auto' }}
          loading={nodeContextBrowserLoading}
          renderItem={(entry) => (
            <List.Item
              style={{ cursor: 'pointer' }}
              onClick={async () => {
                const ok = await copyToClipboard(entry.token);
                if (ok) {
                  message.success(`Copied ${entry.token}`);
                } else {
                  message.error('Failed to copy to clipboard');
                }
                insertIntoLastFocusedNodeField(entry.token);
              }}
            >
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ fontWeight: 600 }}>{entry.key}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {(() => {
                      const src = entry.sources || [];
                      const labels = [];
                      if (src.includes('local_node')) labels.push('local');
                      if (src.includes('workflow_definition')) labels.push('saved');
                      if (src.includes('workflow_node_data')) labels.push('node-data');
                      if (src.includes('historical_job_context')) labels.push('history');
                      const labelText = labels.length ? labels.join(', ') : 'unknown';
                      return `${labelText}${entry.sourceField ? ` (${entry.sourceField})` : ''}`;
                    })()}
                  </div>
                </div>
                <div style={{ marginTop: 4, fontSize: 12, color: '#999' }}>
                  <code style={{ background: '#fafafa', padding: '2px 6px', borderRadius: 4 }}>{entry.token}</code>
                </div>
                {entry.value ? (
                  <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>
                    Value: <span style={{ fontFamily: 'monospace' }}>{String(entry.value).slice(0, 140)}{String(entry.value).length > 140 ? '…' : ''}</span>
                  </div>
                ) : null}
              </div>
            </List.Item>
          )}
        />
        <div style={{ marginTop: 8, fontSize: 11, color: '#999' }}>
          Click a key to copy its token and insert it into the last focused field.
        </div>
      </Modal>

      {/* Version History Modal */}
      <Modal
        title="Version History"
        open={isHistoryModalOpen}
        onCancel={()=>setIsHistoryModalOpen(false)}
        footer={null}
        width={700}
      >
        <List
          size="small"
          dataSource={versionHistory}
          renderItem={(item)=> (
            <List.Item
              actions={[
                <Button size="small" onClick={()=>viewVersion(item.version)}>View</Button>,
                <Button 
                  size="small" 
                  icon={<DiffOutlined />} 
                  onClick={()=>viewDiff(item.version)}
                  disabled={item.version === 1}
                >
                  Diff
                </Button>
              ]}
            >
              <div style={{ display:'flex', flexDirection:'column' }}>
                <span>(v{item.version} - {item.name})</span>
                <small>Edited {new Date(item.created_at).toLocaleString()} by {item.updated_by_username || 'Unknown'}</small>
              </div>
            </List.Item>
          )}
        />
      </Modal>

      {/* Rename Workflow Modal */}
      <Modal
        title="Rename Workflow"
        open={isRenameModalOpen}
        onOk={renameWorkflow}
        onCancel={() => { setIsRenameModalOpen(false); setRenameWorkflowId(null); setRenameWorkflowName(''); }}
        okText="Save"
      >
        <InputWithTemplateButton
          placeholder="Enter new workflow name"
          value={renameWorkflowName}
          onChange={(e) => setRenameWorkflowName(e.target.value)}
          onPressEnter={renameWorkflow}
        />
      </Modal>

      {/* Diff Modal */}
      <Modal
        title={`Changes in Version ${diffVersion}`}
        open={isDiffModalOpen}
        onCancel={() => setIsDiffModalOpen(false)}
        footer={null}
        width={800}
      >
        <DiffViewer diff={currentDiff} />
      </Modal>

      {/* Note Settings Modal */}
      <Modal
        title="Edit Note"
        open={isNoteModalOpen}
        onOk={saveNoteSettings}
        onCancel={() => {
          setIsNoteModalOpen(false);
          setEditingNote(null);
          noteForm.resetFields();
        }}
        okText="Save Note"
        width={600}
      >
        <Form
          form={noteForm}
          layout="vertical"
        >
          <Form.Item
            name="noteText"
            label="Note Text"
            rules={[{ required: true, message: 'Note text is required' }]}
          >
            <Input.TextArea 
              rows={6}
              placeholder="Enter your note text here..."
              style={{ fontFamily: 'inherit' }}
            />
          </Form.Item>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item
              name="backgroundColor"
              label="Background Color"
            >
              <Select>
                <Option value="#fff9c4">💛 Yellow</Option>
                <Option value="#f3e5f5">💜 Purple</Option>
                <Option value="#e1f5fe">💙 Blue</Option>
                <Option value="#e8f5e9">💚 Green</Option>
                <Option value="#ffe0b2">🧡 Orange</Option>
                <Option value="#ffebee">❤️ Pink</Option>
                <Option value="#f5f5f5">⚪ Gray</Option>
                <Option value="#ffffff">⚪ White</Option>
              </Select>
            </Form.Item>
            
            <Form.Item
              name="textColor"
              label="Text Color"
            >
              <Select>
                <Option value="#333">⚫ Dark</Option>
                <Option value="#666">⚫ Gray</Option>
                <Option value="#1890ff">🔵 Blue</Option>
                <Option value="#52c41a">🟢 Green</Option>
                <Option value="#fa8c16">🟠 Orange</Option>
                <Option value="#f5222d">🔴 Red</Option>
                <Option value="#722ed1">🟣 Purple</Option>
              </Select>
            </Form.Item>
          </div>
          
          <div style={{ 
            padding: '8px 12px', 
            background: '#f0f8ff', 
            borderRadius: 4,
            fontSize: 12,
            color: '#666',
            marginTop: 8
          }}>
            💡 <strong>Tips:</strong>
            <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
              <li>Click on the note text to edit inline</li>
              <li>Select the note and drag the resize handles on the edges</li>
              <li>Press Ctrl+Enter to save while editing</li>
              <li>Press Escape to cancel editing</li>
            </ul>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default WorkflowEditor;
